import {
  PLAYER_EVENTS,
  PLAYER_EVENTS_PUBLIC,
  PLAYER_EVENT_PREFIX,
  PLAYER_EVENTS_TO_FORWARD,
  TRACKING_COOKIES,
  SURF_FRAME_EVENTS,
  FLOATING_PLAYER_NAVIGATION_MODES,
  ORIENTATION,
  VIDEO_ORIENTATION,
  MINIMIZED_POSITION,
  MINIPLAYER_SIZE,
} from './constants';
import { BambuserLiveShoppingError } from './errors/BambuserLiveShoppingError';
import Bowser from 'bowser';
import {
  isUndesiredBrowser,
  tryOpenInExternalBrowser,
  isAppleMobileDevice,
  isMacWithMultiTouch,
  isIosWebView,
} from '../../player/src/shared/utils/browser';
import { isFullscreen, enterFullscreen, exitFullscreen, listenToFullscreenEvents } from './utils/fullscreen.js';
import { readCookie, writeCookie } from './utils/cookies';
import CreateProduct from './utils/productSpecGenerator';
import { SchemaModel } from './utils/schemaModel/SchemaModel';
import {
  BambuserLSLegacyConfigurationTransformer,
  BambuserLiveShoppingPlayerAPIDelegate,
  BambuserLiveShoppingPlayerAPIEmbedInstanceAdapter,
} from './playerApi';
import { Logger } from './logging/Logger';
import { EDGE, MIN_WIDTH, MAX_WIDTH_FRACTION, SNAP, QUICK_MOVE } from './config';
import { isActionKey, isBackNav, isForwardNav } from './utils/keyboard';
import Navigation from './utils/navigation';
import WARNING from './constant/warning';
import { deepMergeObjects, getNodeTopParent, isValidEventIdFormat, isValidVideoIdFormat } from './utils/helpers';
import { initGA, initGAConfig, bamlsTag, bamlsTagData } from './utils/analytics';
import { generatePlayerIframeUrlForEmbedInstance } from './utils/playerUrl';
import {
  defaultCloseIcon,
  defaultRestoreIcon,
  getIOSActivePlayerStyles,
  getStyles,
  getStylesInline,
  defaultCloseIconV2,
} from './styles';
import { detectWKWebView, uuid as uuidv4 } from './utils/browser.js';
import PostMessageConduit from './PostMessageConduit.js';
import { BrowserHistoryManager } from './utils/BrowserHistoryManager.js';
import { injectLdJsonSchemaScriptTag } from './utils/seo.js';

const isDev = process.env.NODE_ENV === 'development';
const INTERNAL_EMBED_ORIGINS = /^https?:\/\/([\w-]+\.)*bambuser\.com(:\d+)?$/;

const MINIMIZE_TRANSITION_TIME = 300;
const DEFAULT_PLAYER_ACCESSIBILITY_TITLE = 'Bambuser Live Shopping Player';
const MINI_PLAYER_PLAY_ICON = `
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M8 5v14l11-7z" fill="white"></path>
  </svg>`;
const MINI_PLAYER_PAUSE_ICON = `
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="white"></path>
  </svg>`;
const MINI_PLAYER_SOUND_ON_ICON = `
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M4 9v6h4l5 4V5L8 9H4z" fill="white"></path>
    <path
      d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12"
      fill="none"
      stroke="white"
      stroke-width="2"
      stroke-linecap="round"
    ></path>
  </svg>`;
const MINI_PLAYER_SOUND_OFF_ICON = `
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M4 9v6h4l5 4V5L8 9H4z" fill="white"></path>
    <path
      d="M17 9l4 4M21 9l-4 4"
      fill="none"
      stroke="white"
      stroke-width="2"
      stroke-linecap="round"
    ></path>
  </svg>`;
const urlParams = new URLSearchParams(window.location.search);
let instanceCounter = 1;

const getCurrentHistoryState = () => (
  history.state && typeof history.state === 'object'
    ? history.state
    : {}
);
const PLAYER_HISTORY_BRIDGE_MARKER = '___IFRAME_DEPTH_BRIDGE___';
const isPlayerHistoryBridgeState = (state) => (
  !!state && typeof state === 'object' && state[PLAYER_HISTORY_BRIDGE_MARKER] === true
);

const { MANUAL } = FLOATING_PLAYER_NAVIGATION_MODES;
const { NORTH, SOUTH, EAST, WEST, NORTH_EAST, SOUTH_EAST, SOUTH_WEST, NORTH_WEST } = ORIENTATION;

// Intialize a shared queue for all embed instances
window._bamMetricsQueue ||= [];

export const getMiniPlayerPlaybackControls = ({ playbackStatus = {}, theme = {} } = {}) => {
  const generic = theme.generic || {};
  const isEnded = playbackStatus.ended === true;
  const isRecordedPlayback = playbackStatus.started === true
    && playbackStatus.archived === true
    && playbackStatus.live !== true;
  const showPlayPause = isRecordedPlayback && !isEnded;
  const showSound = playbackStatus.started === true
    && playbackStatus.hasAudio !== false
    && playbackStatus.hideVolumeButton !== true
    && !isEnded;
  const playPauseAction = playbackStatus.playing ? 'pause' : 'play';
  const soundAction = playbackStatus.muted ? 'unmute' : 'mute';

  return {
    playPause: {
      action: playPauseAction,
      ariaLabel: playbackStatus.playing
        ? generic.videoPlayingAriaLabel || 'Pause video'
        : generic.videoPausedAriaLabel || 'Play video',
      icon: playbackStatus.playing ? MINI_PLAYER_PAUSE_ICON : MINI_PLAYER_PLAY_ICON,
      pressed: playbackStatus.playing === true,
      visible: showPlayPause,
    },
    sound: {
      action: soundAction,
      ariaLabel: playbackStatus.muted
        ? generic.soundMutedAriaLabel || 'Turn on sound'
        : generic.soundUnmutedAriaLabel || 'Turn sound off',
      icon: playbackStatus.muted ? MINI_PLAYER_SOUND_OFF_ICON : MINI_PLAYER_SOUND_ON_ICON,
      pressed: playbackStatus.muted === true,
      visible: showSound,
    },
  };
};

class BambuserLiveShopping {
  static publicAPIEndpointsURL = process.env.WIDGETS_APPENGINE_BASE_URL;

  constructor(config) {
    const browser = Bowser.getParser(window.navigator.userAgent);
    // Warning: Detecting desktop browser currently skips any custom platform.
    this.isDesktop = browser.getPlatformType(true) === 'desktop';
    this.isNonSupportedBrowser = browser.satisfies({
      windows: { 'internet explorer': '<=11' },
      mobile: { safari: '<11' },
    });

    this.debug = (isDev && urlParams.get('debug')) || urlParams.get('debug-prod');
    this.logger = new Logger({
      logPrefix: `[EMBED${window.top === window ? '-TOP' : ''}] `,
      debug: this.debug,
      isDev,
    });

    this.setConfig(config, browser);

    window.__bambuserSetEvent = (eventId) => {
      this._setAppContextInCurrentPlayer({ eventId });
    };

    this._frames = []; // Keep track of all iframes (players and bam-product instances) connected to this embed instance
    this.instanceId = instanceCounter++;
    this.instanceKey = uuidv4();
    this.landingPagePageTitle = document.title;
    this.historyPollingInterval = null; // To sync history with surf behind iframe href

    // surf behind
    this.surf = {
      frame: null,
      container: null,
      containerId: null,
      skipNextBrowserHistoryEntry: false,
    };
    this.player = {
      frame: null,
      container: null,
      containerId: null,
    };
    this.timeline = {
      isLive: false,
      currentTime: 0,
      currentBroadcastId: null,
      serverTimeAtCurrentPosition: 0,
    };
    this.playerSettings = {
      preferNewTabCheckout: true,
    };
    this.video = {
      videoOrientation: VIDEO_ORIENTATION.VERTICAL,
    };

    let snapHorizontalThresholdRatio, snapVerticalThresholdRatio, minWidth, thresholdX, thresholdY;

    if (this.isDesktop) {
      snapHorizontalThresholdRatio = SNAP.HORIZONTAL_THRESHOLD_ON_DESKTOP;
      snapVerticalThresholdRatio = SNAP.VERTICAL_THRESHOLD_ON_DESKTOP;
      minWidth = MIN_WIDTH.SMALL.DESKTOP;
      thresholdX = QUICK_MOVE.THRESHOLD_X_DESKTOP;
      thresholdY = QUICK_MOVE.THRESHOLD_Y_DESKTOP;
    } else {
      snapHorizontalThresholdRatio = SNAP.HORIZONTAL_THRESHOLD_ON_MOBILE;
      snapVerticalThresholdRatio = SNAP.VERTICAL_THRESHOLD_ON_MOBILE;
      minWidth = MIN_WIDTH.SMALL.MOBILE;
      thresholdX = QUICK_MOVE.THRESHOLD_X_MOBILE;
      thresholdY = QUICK_MOVE.THRESHOLD_Y_MOBILE;
    }

    this.miniPlayer = {
      position: { x: 0, y: 0 },
      requestedPosition: { x: 0, y: 0 },
      snapHorizontalThresholdRatio,
      snapVerticalThresholdRatio,
      snapThresholdOff: SNAP.THRESHOLD_OFF,
      minWidth,
      maxX: 0,
      maxY: 0,
    };

    this.quickMove = {
      enabled: QUICK_MOVE.ENABLED,
      orientation: '',
      targetedPosition: {},
      thresholdX,
      thresholdY,
      trustIndex: 0,
      timeout: null,
      detectionCount: 0,
    };

    this.onMiniPlayerMouseEnterLeave = this.onMiniPlayerMouseEnterLeave.bind(this);
    this.onFirstElementKeyDown = this.onFirstElementKeyDown.bind(this);
    this.onLastElementKeyDown = this.onLastElementKeyDown.bind(this);

    const playerAPIEmbedInstanceAdapter = new BambuserLiveShoppingPlayerAPIEmbedInstanceAdapter(this);
    this.playerDelegate = new BambuserLiveShoppingPlayerAPIDelegate(playerAPIEmbedInstanceAdapter);
    if (window.location.search.match(/(\?|&)bambuserAppDevEnv=1(&.*)?$/)) this._isAppDevEnv = true;

    this._pendingRequestsToPlayer = {};
    this._shouldClosePlayerOnLastBackNavigation = true;
  }

  _setAppContextInCurrentPlayer({ eventId, channelId }) {
    if (!eventId) return;

    this.createLoader(this.player.container, true);

    const originalEventId = this.config.eventId || this.config.videoId;
    const queryString = generatePlayerIframeUrlForEmbedInstance(this, true,
      {
        channelId,
        originalEventId,
        eventId,
      }
    );
    this._sendMessageToPlayer(PLAYER_EVENTS.SET_APP_CONTEXT, {
      queryString,
    });
  }

  setConfig(config, browser) {
    this.config = config || {};
    // remove the hash so we don't add query parameters after it
    this.config.playerUrl = (this.config.playerUrl || process.env.WEBPLAYER_BASE_URL || '').split('#')[0];

    // Let developers insist on a specific player base URL by
    // setting a value in localStorage
    try {
      const playerUrlOverride = window.localStorage.getItem('bambuserPlayerBaseURLOverride');
      if (playerUrlOverride.match(/^https?:\/\//)) {
        this.config.playerUrl = playerUrlOverride;
      }
    } catch (err) {
      // This is fine
    }

    this.config.mockLive = urlParams.get('mockLiveBambuser');
    this.config.timestamp = urlParams.get('time');
    this.config.broadcastHash = urlParams.get('br');
    // iOS fullscreen is non standard and doesn't work on non-video elements
    // https://developer.apple.com/forums/thread/133248
    // NOTE: AS of early 2025, iOS supports fullscreen but must be explicitly enabled by the user
    const allowFullscreen = browser.getOSName() !== 'iOS';
    this.config.allowFullscreen = allowFullscreen ? urlParams.get('allowFullscreen') : '0';
    this.config.startFullscreen = urlParams.get('startFullscreen')?.match(/^(1|true)$/i);
    this.config.playerVersion = this.playerVersion;
    this.config.isDashboard = this.isDashboard;
    this.config.allowShareAutoplay = this.isAutoplayAllowed();
    this.config.destroyOnAppUnload = this.config.destroyOnAppUnload ?? true;
    if (this.config.withGA) initGA(this.config.withGA);
  }

  async show(replaceExisting = false) {
    if (replaceExisting && this.hasBeenShown) {
      this.destroy();
    }

    if (this.hasBeenShown) {
      this.logger.log('hasBeenShown');
      // Ensures that the original show is loaded/restored even thus some other show may have been loaded in between.
      // Ex. when browsing the page in a SPA where no surf iframe should be present,
      // the user might press the original button again with same BambuserLiveShopping instance.
      if (this.__restoreMinimized && this.__originalEventId) {
        this.loadShow(this.__originalEventId);
      }
      return false; // Already been shown, so it should already exist!?
    }

    const { href: currentUrl, search: currentSearch } = window.location;

    // For some rare cases when user would be surfing around on merchant website with a minimized player and decides to
    // start another show, it should not start playing in that iframe and create another player / surf iframe etc.
    // Instead we ensure to reload the whole page discarding this player and surf session with a new one for the target
    // show in top level window. We do this mainly prevent having a deep iframe nesting with surf iframe having a player
    // having a surf iframe... But not to also have more than one show active at one specific time.
    //
    // Normally we would like to start playing from the top frame, but for app dev env where the merchant page is placed
    // in a sub frame so we need to handle it differently:
    // 1. We don't want below block to execute on initial load (it's achived by relying on auto play parameter that is
    //    used during boot sequence and removed in surf behind iframes)
    // 2. We want the sub merchant page to behave like it's the top frame and below block should not to execute (it's
    //    achived by checking if parent window (app dev env) is top window or not)
    const currentWindowIsTop = window === window.top;
    const haveAutoPlayQueryParam = currentSearch.indexOf('autoplayLiveShopping=') > -1;
    const parentWindowIsTop = window.parent === window.top;
    const allowFixPlayFromNonTopFrame = !currentWindowIsTop && (!this._isAppDevEnv || (
      !haveAutoPlayQueryParam && !parentWindowIsTop
    )) && !this.isDashboard;
    if (allowFixPlayFromNonTopFrame) {
      const targetFrame = this._isAppDevEnv ? window.parent : window.top;
      const handledByParent = await new Promise((resolve) => {
        const requestId = Date.now();
        const config = this.config;
        const fromNode = !!config.node;
        delete config.node;

        // Register a post message handler to receive response from parent window.
        // But we are only interested in one specific event, whether our request to handle
        // opening the show was accepted.
        const parentMessageHandler = (event) => {
          const sourceIsParentWindow = event.source == targetFrame;
          if (!sourceIsParentWindow) {
            this.logger.log('show() parentMessageHandler() exit', event && event.data);
            return;
          }

          let message = null;
          try {
            message = JSON.parse(event.data);
          } catch (e) {
            this.logger.logProd('show() parentMessageHandler() error', e);
          }

          const shouldLogEvent = !message || message.eventName !== PLAYER_EVENTS.ON_PROGRESS;
          if (shouldLogEvent) this.logger.logDev('show() parentMessageHandler() Event from parent', message);

          if (message && message.eventName) {
            const eventName = message.eventName;
            if (eventName === SURF_FRAME_EVENTS.ACCEPTED_REQUEST_OPEN_SHOW && message.data.requestId === requestId) {
              window.clearTimeout(noResponseTimeout);
              window.removeEventListener('message', parentMessageHandler);
              resolve(true);
              return;
            }
          }
          this.logger.log('show() parentMessageHandler() Unhandled message', event);
        };
        parentMessageHandler.bind(this);
        window.addEventListener('message', parentMessageHandler, false);

        // We will wait some time for parent window to respond, otherwise raise a failure!
        const noResponseTimeout = window.setTimeout(() => {
          window.removeEventListener('message', parentMessageHandler);
          resolve(false);
        }, 1500);

        // Send request to parent window to open the show!
        const message = JSON.stringify({
          eventName: SURF_FRAME_EVENTS.REQUEST_OPEN_SHOW,
          data: { requestId, config, fromNode },
        });
        targetFrame.postMessage(message, '*');
      });

      // Player was shown by parent embed instance, bail!
      if (handledByParent) {
        this.logger.log('handledByParent');
        return;
      }

      // As a last resort, we change top url and includes autoplayLiveshopping param.
      // It may autoplay it may not, but user will just have to click once again there and it should work normally.
      // But that would never happen? But better safe than sorry.
      const querySeparator = currentSearch.length > 0 ? '&' : '?';
      return (targetFrame.location = `${currentUrl}${querySeparator}autoplayLiveShopping=${this.config.eventId}`);
    } else if (window.__currentBambuserLiveShoppingOverlayPlayer) {
      // load/restore a show when surfing without iframe in minimized state
      this.logger.log('load/restore a show when surfing without iframe in minimized state');
      const newEventId = this.config.eventId;
      const overlayPlayer = window.__currentBambuserLiveShoppingOverlayPlayer;
      const isNewShow = overlayPlayer.config.eventId !== newEventId;
      if (isNewShow) {
        // Mounting the API again will make new call to the mountPoint with the new eventId and make the
        // getProviderConfig method return a config specific to that new event
        overlayPlayer.mountPlayerAPI(newEventId, true, overlayPlayer.player?.container ?? null);
      }

      // Fetch the provider config for the show which is about to load
      // and merge it with any new configs which might be passed in the request
      // (notably deeplink is an interesting property which we want the new player config
      // to properly update).
      const providerConfig = overlayPlayer.getProviderConfig();
      // Allow to disable autoplay from the player configuration (only if autoplay is allowed for the customer)
      if (this.config.allowShareAutoplay) {
        if (providerConfig.allowShareAutoplay === false) this.config.allowShareAutoplay = false;
      } else if (providerConfig.allowShareAutoplay === true) {
        console.warn(WARNING.PLAYER_CONFIG_IGNORED_BY_ORGANIZATION_CONFIG('allowShareAutoplay', 'autoplayHandler'));
      }
      overlayPlayer.config = Object.assign(providerConfig, this.config);

      return overlayPlayer.loadShow(newEventId, isNewShow);
    }

    this._emitEvent('load');

    const undesiredBrowser = isUndesiredBrowser({ _isEmbedContext: true });
    if (undesiredBrowser && this.config.eventId && !urlParams.has('autoplayLiveShopping')) {
      this.logger.log('history.pushState show()');
      const querySeparator = document.location.search.length > 0 ? '&' : '?';
      const urlWithAutoplay = `${currentUrl}${querySeparator}autoplayLiveShopping=${
        this.config.eventId
      }&bls_ubn=${encodeURIComponent(undesiredBrowser.name)}`;

      try {
        return await new Promise((resolve, reject) =>
          tryOpenInExternalBrowser(urlWithAutoplay, {
            onFail: () => reject(new Error('Failed to open in external browser')),
          }),
        );
      } catch (err) {
        // Open in browser failed: render player, let it do another attempt & eventually fail to an info screen.
        this.logger.logProd('embed.js failed to open external browser', err);
      }

      // Add the current page in the browser history, and update the browser URL with the autoplay URL
      window.history.pushState(null, null, urlWithAutoplay);
    }

    this.config.sourceUrl = currentUrl;

    const containerId = `livecommerce-${this.instanceId}-${new Date().getTime()}`;
    const surfContainerId = this.surf.containerId || `livecommerce-surf-${this.instanceId}-${new Date().getTime()}`;
    const backgroundContainerId = `livecommerce-bg-${this.instanceId}-${new Date().getTime()}`;

    const container = this?.config?.containerNode || document.createElement('div');
    container.setAttribute('id', containerId);
    container.setAttribute('data-bambuser-liveshopping-player-id', containerId);

    this.createLoader(container);

    this.styleElem = document.createElement('style');
    this.styleElem.setAttribute('type', 'text/css');

    this.activePlayerStyleElem = document.createElement('style');
    this.activePlayerStyleElem.setAttribute('type', 'text/css');

    const activePlayerStyles = getIOSActivePlayerStyles();
    let styles = getStyles(containerId, surfContainerId, backgroundContainerId, this.isDesktop);

    if (this.config.type === 'inline') {
      styles += getStylesInline(containerId, this.config.height || '550px');
    }

    'textContent' in this.activePlayerStyleElem
      ? (this.activePlayerStyleElem.textContent = activePlayerStyles)
      : (this.activePlayerStyleElem.styleSheet.cssText = activePlayerStyles);
    document.head.appendChild(this.activePlayerStyleElem);

    'textContent' in this.styleElem
      ? (this.styleElem.textContent = styles)
      : (this.styleElem.styleSheet.cssText = styles);
    document.head.appendChild(this.styleElem);

    // Add container differently depending on the type
    if (this.config.type === 'inline') {
      if (!this.config.node) {
        throw new BambuserLiveShoppingError("A 'node' must be specified for the inline type!");
      }
      container.classList.add('inline');
      this.config.node.appendChild(container);
    } else {
      container.style.position = 'fixed';
      container.style.bottom = 0;
      container.style.right = 0;
      container.style.width = '100%';
      container.style.height = '100%';
      //container.style.WebkitOverflowScrolling = 'touch';
      //container.style.overflow = 'auto';
      container.style.overscrollBehavior = 'none';
      // Power grab as some other component on customers site is at z-index: 2147483646. Lets hit 2147483647 (max)
      container.style.zIndex = 2147483647;

      if (!this.config.containerNode) {
        const playerContainerNode =
          (typeof this.config.playerContainerNode === 'object' &&
            this.config.playerContainerNode.tagName &&
            this.config.playerContainerNode) ||
          document.body;
        playerContainerNode.appendChild(container);
      }

      this._disableParentScroll();
      document.addEventListener('touchmove', (this.__preventBodyScroll = this._preventBodyScroll.bind(this)), {
        passive: false,
      });

      // Send any base page scroll position to player on iOS
      // (to allow syncing video top with browser top when writing in chat).
      // prettier-ignore
      if (isAppleMobileDevice() || isMacWithMultiTouch()) {
        window.visualViewport
          ? window.visualViewport.addEventListener('resize', this._viewportChangeHandler.bind(this))
          : window.addEventListener(
            'scroll', // Fallback for iOS version 12 and below
            (this.__pushPageScrollTopHandler = this._pushPageScrollTopHandler).bind(this),
          );
      }
    }

    this.hasBeenShown = true;
    this.__originalEventId = this.config.eventId;
    this.surf.containerId = surfContainerId;
    this.backgroundContainerId = backgroundContainerId;
    this.player.container = container;
    this.player.containerId = containerId;

    this.__cancelFullscreenListener = this.listenToPlayerFullscreenEvents();

    const isSiteReady = document.readyState !== 'loading';
    if (isSiteReady) {
      this.loadPlayer();
    } else {
      this.loadPlayerBound = this.loadPlayer.bind(this);
      window.addEventListener('DOMContentLoaded', this.loadPlayerBound);
    }

    return true;
  }

  _getPlayerAccessibilityLabels(config = this.config) {
    const externalTitle = config?.externalTitle || DEFAULT_PLAYER_ACCESSIBILITY_TITLE;

    return {
      externalTitle,
      innerTitle: config?.innerTitle || externalTitle,
    };
  }


  _createPlayerFrame() {
    this.logger.log('_createPlayerFrame()');
    const { externalTitle } = this._getPlayerAccessibilityLabels();
    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', this._getPlayerUrl());
    iframe.setAttribute('frameborder', 0);
    iframe.setAttribute('allowTransparency', true);
    iframe.setAttribute('allow', 'fullscreen; autoplay; web-share');
    iframe.setAttribute('title', externalTitle);
    if (this.config.sandboxAttributes) iframe.setAttribute('sandbox', this.config.sandboxAttributes);
    if (this.config.credentiallessIframes) iframe.setAttribute('credentialless', true);
    return iframe;
  }

  // Enable new public api
  mountPlayerAPI(eventId, force = false, playerElement = null) {
    const mountPoint = 'onBambuserLiveShoppingReady';
    try {
      this.playerDelegate.mount(window, mountPoint, eventId, force, { playerElement });
    } catch (e) {
      //this.logger.warnProd(`No ${mountPoint} method was found on window. Using default values for player.`);
    }

    // Notify any code running on embedding site that a new instance is ready so it knowns when instance methods can to
    // be called, ex. configure or setTrackingTags from GTM scripts. This may happen multiple times during a player's
    // lifecycle, so we keep track of it and only send same event when forced
    this.publishedInstanceReady = this.publishedInstanceReady || {};
    if (this.publishedInstanceReady[this.instanceKey] === eventId && !force) return;
    if (!this.playerDelegate.isMounted()) {
      // When having a non mounted player (ex. global method doesn't exist) ensure that the same logic of removing any
      // listeners are cleared before notifying embedding site so no duplicate events are accidentially triggered
      this.playerDelegate.publicAPI.removeAllListeners();
    }
    this.publishedInstanceReady[this.instanceKey] = eventId;
    const instanceReadyEvent = new CustomEvent('bambuser-instance-ready', {
      detail: {
        ...(this.config.isVod ? {
          type: 'vod-player',
          instanceKey: `vod-player-${this.instanceKey}`,
          videoId: eventId,
        } : {
          type: 'player',
          instanceKey: `show-player-${this.instanceKey}`,
          showId: eventId,
        }),
        instance: this.playerDelegate.publicAPI,
      },
    });
    document.dispatchEvent(instanceReadyEvent);
  }

  loadPlayer() {
    const { eventId } = this.config;
    if (this._didLoadPlayer) return;
    this._didLoadPlayer = true;

    if (!isValidEventIdFormat(eventId) && !isValidVideoIdFormat(eventId)) {
      console.warn(`${eventId} does not seem to have a valid show or video Id format`);
    }

    this.logger.log('loadPlayer() eventId', eventId);

    if (typeof this.loadPlayerBound === 'function') {
      window.removeEventListener('DOMContentLoaded', this.loadPlayerBound);
      delete this.loadPlayerBound;
    }

    this.mountPlayerAPI(eventId, false, this.player?.container ?? null);
    this.applyProviderConfig();

    if (this.config.checkoutOnCartClick) {
      const checkoutEventName = PLAYER_EVENTS.CHECKOUT.replace(PLAYER_EVENT_PREFIX, '');
      const checkoutOnCartClick = this.config.checkoutOnCartClick;

      if (!this.playerDelegate.getSupportedEvents().includes(checkoutEventName)) {
        let warningMsg = `When the checkoutOnCartClick configuration attribute is set to ${checkoutOnCartClick}`;
        warningMsg += 'an event listener for player.EVENT.CHECKOUT must be registered.';
        console.warn(warningMsg);
      }
    }

    if (this.config.containerNode) {
      this.player.frame = this.config.containerNode.getElementsByTagName('iframe')[0];
    } else {
      this.player.frame = this._createPlayerFrame();
      this.player.container.appendChild(this.player.frame);
      if (this.config.type === 'overlay') {
        window.__currentBambuserLiveShoppingOverlayPlayer = this;
      }
    }

    this.connectToIframe(this.player.frame);

    // If in app dev env, set up message passing between player and parent frame
    if (this._isAppDevEnv) {
      this.__processAppDevEnvMessage = this._processAppDevEnvMessage.bind(this);
      PostMessageConduit.registerGlobalListener(this.__processAppDevEnvMessage);
    }

    // Try ensure site doesn't have any other active playbacks as show now should have focus.
    this._pauseOtherPlaybacks();

    // Listen to go back navigation events from BrowserHistoryManager and forward
    // them to the player which will update the UI accordingly
    this._onGoBack = () => {
      // If depth is zero we should not forward the event to the player but instead unload the player
      // and reset the history state
      if (BrowserHistoryManager.getDepth() === 0) {
        if (this._shouldClosePlayerOnLastBackNavigation) {
          this.close();
          return;
        }
      }
      this._sendMessageToPlayer(PLAYER_EVENTS.NAVIGATION_GO_BACK);
    };
    BrowserHistoryManager.addEventListener('goBack', this._onGoBack);
  }

  connectToIframe(iframe) {
    // Setup postMessage listener for messages coming from the given iframe
    this._frames.push(iframe);
    PostMessageConduit.registerIframeListener(iframe, this._processReceivedMessage);
  }

  applyProviderConfig() {
    const providerConfig = this.getProviderConfig();

    if (providerConfig._useExternalPlayerController && this.player.container) {
      this.player.container.style.backgroundColor = 'transparent';
    }

    // Allow to disable autoplay from the player configuration (only if autoplay is allowed for the customer)
    if (this.config.allowShareAutoplay) {
      if (providerConfig.allowShareAutoplay === false) this.config.allowShareAutoplay = false;
    } else if (providerConfig.allowShareAutoplay === true) {
      console.warn(WARNING.PLAYER_CONFIG_IGNORED_BY_ORGANIZATION_CONFIG('allowShareAutoplay', 'autoplayHandler'));
    }
    this.config = Object.assign(providerConfig, this.config);

    if (this.config.enableFirstPartyCookies) {
      this._trackingContext = Object.assign(this._trackingContext || {}, {
        userId: this.config._externalProvidedUid || readCookie(TRACKING_COOKIES.USER_ID) || uuidv4(),
        sessionId: readCookie(TRACKING_COOKIES.SESSION_ID) || uuidv4(),
      });
      if (this.config.withGA) initGAConfig(this._trackingContext.userId);
    }
  }

  /**
   * Configuration is provided by hooking into the
   * window.onBambuserLiveShoppingReady callback and configuring
   * the player via the playerApi.
   */
  getProviderConfig() {
    const configTransformer = new BambuserLSLegacyConfigurationTransformer(this.playerDelegate);
    const providerConfig = this.playerDelegate.config.getConfigUsingTransformer(configTransformer);

    if (!this.playerDelegate.isMounted()) {
      // Missing onBambuserLiveShoppingReady
      // Make sure we don't use withoutProductDetails, due to the legacy reasons
      // It's important on the shopify integration side
      providerConfig.withoutProductDetails = false;
    }

    const { edgeSpacing = {} } = providerConfig || {};
    const spacing = this.isDesktop ? EDGE.SPACING_DESKTOP : EDGE.SPACING_MOBILE;
    const { top = spacing, right = spacing, bottom = spacing, left = spacing } = edgeSpacing[this.deviceType] || {};
    providerConfig.edgeSpacing = { top, right, bottom, left };

    return JSON.parse(JSON.stringify(providerConfig || {}));
  }

  _onPlayerApiEventHandlerAdded(eventName) {
    // Keep track of all event handlers added by the embedding page.
    // We'll send the names over the player once it has started.
    this._playerEventHandlers = this._playerEventHandlers || [];
    if (!this._playerEventHandlers.includes(eventName)) {
      this._playerEventHandlers.push(eventName);
    }
  }

  _pollHistoryFromSurfIframe() {
    try {
      const currentUrl = this.surf.frame.contentWindow.location.href;
      if (currentUrl !== this.surf.lastUrl && currentUrl !== 'about:blank') {
        this.surf.lastUrl = currentUrl;
        // pushState & replaceState doesn't really support the title (2nd) parameter, so we force it manually
        const title = this.surf.frame.contentWindow.document.title || document.title;
        document.title = title;
        if (currentUrl === window.location.href) return;
        // Browsers remember the iframe navigation in back/forward buttons.
        // We add "Bambuser Live Shopping flag" to indicate that this is a navigation happening inside the iframe so
        // we can detect when to show/hide iframe when moving back/forward in history.
        // Update the browser URL with the product page URL, but don't alter the browser history
        window.history.replaceState({ ...getCurrentHistoryState(), bambuserLiveShopping: true }, null, currentUrl);
      }
    } catch (e) {
      // Cross-origin access, stop polling
      if (this.historyPollingInterval) {
        window.clearInterval(this.historyPollingInterval);
        this.historyPollingInterval = null;
        this.logger.logProd('Cross-origin content in surf iframe, stopping history polling.', e.message);
      }
    }
  }

  showPlayer() {
    this.logger.log('showPlayer()');
    if (this.player.frame) {
      // Log successful use of tryOpenInExternalBrowser()
      const urlParams = new URLSearchParams(window.location.search);
      const sourceBrowser = urlParams.get('bls_ubn');
      if (sourceBrowser) {
        const escapeMethod = urlParams.get('bls_ubm');
        this._sendMessageToPlayer(PLAYER_EVENTS.OPEN_EXTERNAL_BROWSER_SUCCESS, {
          bls_ubn: sourceBrowser,
          bls_ubm: escapeMethod,
        });
      }

      this.player.frame.classList.add('ready');
      this.hideLoader();
      this.player.frame.setAttribute('data-test-id', 'iframePlayer');

      // Maybe add viewport fit cover for iOS devices, by doing this we allow webviews to hopefully use the safe area insets
      this._maybeAddViewportFitCover();

      this._emitEvent(PLAYER_EVENTS_PUBLIC.READY);

      if (isIosWebView()) {
        // Forcefully focus on the player when running inside a WebView on iOS
        this.player.frame.focus();
      }
    }
  }

  createLoader(container) {
    if (!this.loader) {
      this.loader = document.createElement('div');
      this.loader.classList.add('livecommerce-loader');
      window.setTimeout(() => this?.loader?.classList.add('fade-in'), 1000);
    } else {
      this.loader.classList.remove('hidden');
      this.loader.classList.add('visible');
    }

    // We use a separate div that fills the container while loading the iframe
    // otherwise click events on the container background is not detected on iOS (????!!)
    if (!this.loaderWrapper) {
      this.loaderWrapper = document.createElement('div');
      this.loaderWrapper.classList.add('livecommerce-loader-wrapper');
      this.loaderWrapper.style.width = '100%';
      this.loaderWrapper.style.height = '100%';
    }

    if (!this.config.disableClickOutsideBehavior) {
      this.loaderWrapper.addEventListener('click', this.unloadPlayerApp.bind(this));
    }

    if (!this.config.containerNode) {
      if (!container.contains(this.loader)) {
        container.appendChild(this.loader);
      }

      if (!container.contains(this.loaderWrapper)) {
        container.appendChild(this.loaderWrapper);
      }
    }
  }

  hideLoader() {
    if (document.contains(this.loaderWrapper)) {
      this.loaderWrapper.parentNode.removeChild(this.loaderWrapper);
      this.loaderWrapper = null;
    }
    this.loader?.classList.remove('visible');
    this.loader?.classList.add('hidden');
  }

  _maybeAddViewportFitCover() {
    // REMARK: For now, let's not apply viewport-fit=cover for embeds which is configured as vod context.
    const { isWebComponent, useViewportFitScale = false } = this.config || {};
    if (isWebComponent && !useViewportFitScale) return;

    const isIOSWebView = (isAppleMobileDevice() || isMacWithMultiTouch()) && detectWKWebView();
    if (isIOSWebView || useViewportFitScale) {
      let metaTag = document.querySelector('meta[name="viewport"]');

      // If the meta tag exists, update its content
      if (metaTag) {
        const content = metaTag.getAttribute('content');

        // Check if viewport-fit is already present
        if (!content.includes('viewport-fit=cover')) {
          metaTag.setAttribute('content', `${content}, viewport-fit=cover`);
        }

        // Store the previous content to be able to revert back
        this._previousViewportContent = content;
      } else {
        // If the meta tag doesn't exist, create it
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', 'viewport');
        metaTag.setAttribute('content', 'viewport-fit=cover');
        document.head.appendChild(metaTag);
      }
    }
  }

  _resetBackToPreviousViewportContent() {
    if (this._previousViewportContent) {
      const metaTag = document.querySelector('meta[name="viewport"]');
      metaTag.setAttribute('content', this._previousViewportContent);
    }
  }

  syncCartState() {
    this._emitEvent(PLAYER_EVENTS_PUBLIC.SYNC_CART_STATE, (data) => {
      // TODO: Implement more sophisticated cart sync with sending all the SKUs to player.
      // Currently we only clear the basket when it has reached zero items (ex. after checkout).
      if (data && data.count === 0) {
        this.updatePlayerCart({ count: 0 });
      }
    });
  }

  /**
   * Sends a message to the player to update cart contents
   * @param {object} data
   * @param {int} data.count number of items in cart
   * @param {array} data.items list of products (id's) that should be in cart
   */
  updatePlayerCart({ count, items }) {
    this._sendMessageToPlayer(PLAYER_EVENTS.DO_SYNC_CART_STATE, { count, items });
  }

  moveMiniPlayer() {
    const { x, y } = this.miniPlayer.position;
    this.player.container.style.transform = `translate(${x}px, ${y}px)`;
  }

  snapToBorders() {
    if (!SNAP.ENABLED) {
      return;
    }

    const {
      position,
      position: { x, y },
      snapLeftThreshold,
      snapTopThreshold,
      snapBottomThreshold,
      minX,
      minY,
      maxX,
      maxY,
      videoWidth,
      videoHeight,
    } = this.miniPlayer;
    let shouldSnap = false;

    const { edgeSpacing } = this.config;

    switch (this.config.minimizedPosition) {
      case MINIMIZED_POSITION.TOP_LEFT:
        if (x < minX + snapLeftThreshold) {
          position.x = minX + edgeSpacing.left;
          shouldSnap = true;
        } else if (x > edgeSpacing.right - snapLeftThreshold) {
          position.x = maxX - edgeSpacing.right - videoWidth;
          shouldSnap = true;
        }

        if (y < minY + snapTopThreshold) {
          position.y = minY + edgeSpacing.top;
          shouldSnap = true;
        } else if (y > maxY - snapBottomThreshold - videoHeight) {
          position.y = maxY - videoHeight - edgeSpacing.bottom;
          shouldSnap = true;
        }
        break;
      case MINIMIZED_POSITION.TOP_RIGHT:
        if (x < minX + snapLeftThreshold) {
          position.x = minX + edgeSpacing.left;
          shouldSnap = true;
        } else if (x > edgeSpacing.right - snapLeftThreshold) {
          position.x = maxX - edgeSpacing.right;
          shouldSnap = true;
        }

        if (y < minY + snapTopThreshold) {
          position.y = minY + edgeSpacing.top;
          shouldSnap = true;
        } else if (y > maxY - snapBottomThreshold) {
          position.y = maxY - edgeSpacing.bottom;
          shouldSnap = true;
        }
        break;
      case MINIMIZED_POSITION.BOTTOM_LEFT:
        if (x < minX + snapLeftThreshold) {
          position.x = minX + edgeSpacing.left;
          shouldSnap = true;
        } else if (x > edgeSpacing.right - snapLeftThreshold) {
          position.x = maxX - edgeSpacing.right - videoWidth;
          shouldSnap = true;
        }

        if (y < minY + snapTopThreshold) {
          position.y = minY + edgeSpacing.top;
          shouldSnap = true;
        } else if (y > edgeSpacing.bottom - snapBottomThreshold) {
          position.y = maxY - edgeSpacing.bottom;
          shouldSnap = true;
        }
        break;
      case MINIMIZED_POSITION.BOTTOM_RIGHT:
      default:
        if (x < minX + snapLeftThreshold) {
          position.x = minX + edgeSpacing.left;
          shouldSnap = true;
        } else if (x > edgeSpacing.right - snapLeftThreshold) {
          position.x = maxX - edgeSpacing.right;
          shouldSnap = true;
        }

        if (y < minY + snapTopThreshold) {
          position.y = minY + edgeSpacing.top;
          shouldSnap = true;
        } else if (y > edgeSpacing.bottom - snapBottomThreshold) {
          position.y = maxY - edgeSpacing.bottom;
          shouldSnap = true;
        }
        break;
    }

    if (shouldSnap) {
      this.player.container.classList.add('is-snapping');
      window.setTimeout(() => this.player.container.classList.remove('is-snapping'), 500);
      this.moveMiniPlayer();
    }
  }

  getQuickMoveOrientation(dx, dy) {
    const x = Math.abs(dx) > this.quickMove.thresholdX;
    const y = Math.abs(dy) > this.quickMove.thresholdY;
    if (!x) {
      if (!y) {
        return '';
      }
      return dy > 0 ? SOUTH : NORTH;
    }

    if (!y) {
      return dx < 0 ? EAST : WEST;
    }

    return dx < 0 ? (dy > 0 ? SOUTH_EAST : NORTH_EAST) : dy > 0 ? SOUTH_WEST : NORTH_WEST;
  }

  // prettier-ignore
  getQuickMoveTargetedPosition(orientation, { x, y }) {
    const { minX, minY, maxX, maxY } = this.miniPlayer;
    const { edgeSpacing } = this.config;

    switch (orientation) {
      case NORTH:
        if (x < minX + edgeSpacing.left) { // Quick move while sticking to the left
          return { x: minX + edgeSpacing.left, y: minY + edgeSpacing.top };
        }
        if (x > maxX - edgeSpacing.right) { // Quick move while sticking to the right
          return { x: maxX - edgeSpacing.right, y: minY + edgeSpacing.top };
        }
        return { x, y: minY + edgeSpacing.top };
      case SOUTH:
        if (x < minX + edgeSpacing.left) { // Quick move while sticking to the left
          return { x: minX + edgeSpacing.left, y: maxY - edgeSpacing.bottom };
        }
        if (x > maxX - edgeSpacing.right) { // Quick move while sticking to the right
          return { x: maxX - edgeSpacing.right, y: maxY - edgeSpacing.bottom };
        }
        return { x, y: maxY - edgeSpacing.bottom };
      case EAST:
        if (y < minY + edgeSpacing.top) { // Quick move while sticking to the top
          return { x: minX + edgeSpacing.left, y: minY + edgeSpacing.top };
        }
        if (y > maxY - edgeSpacing.bottom) { // Quick move while sticking to the bottom
          return { x: minX + edgeSpacing.left, y: maxY - edgeSpacing.bottom };
        }
        return { x: minX + edgeSpacing.left, y };
      case WEST:
        if (y < minY + edgeSpacing.top) { // Quick move while sticking to the top
          return { x: maxX - edgeSpacing.right, y: minY + edgeSpacing.top };
        }
        if (y > maxY - edgeSpacing.bottom) { // Quick move while sticking to the bottom
          return { x: maxX - edgeSpacing.right, y: maxY - edgeSpacing.bottom };
        }
        return { x: maxX - edgeSpacing.right, y };
      case NORTH_EAST:
        return { x: minX + edgeSpacing.left, y: minY + edgeSpacing.top };
      case SOUTH_EAST:
        return { x: minX + edgeSpacing.left, y: maxY - edgeSpacing.bottom };
      case SOUTH_WEST:
        return { x: maxX - edgeSpacing.right, y: maxY - edgeSpacing.bottom };
      case NORTH_WEST:
        return { x: maxX - edgeSpacing.right, y: minY + edgeSpacing.top };
    }
  }

  quickMoveReset() {
    this.quickMove.orientation = ''; // no more quick move in progress
    this.quickMove.trustIndex = 0;
    this.quickMove.detectionCount = 0;
  }

  onMiniPlayerMouseEnterLeave(event) {
    const mouseIsOver = event.type === 'mouseenter';
    this._sendMessageToPlayer(PLAYER_EVENTS.SET_VIEWPORT_STATE, { mouseIsOver });
  }

  _onActionKeyDown(e, callback) {
    if (!isActionKey(e) || typeof callback !== 'function') return;
    e.preventDefault();
    callback();
  }

  onFirstElementKeyDown(e) {
    e.stopPropagation();
    if (!isBackNav(e)) return;
    // emits an event to the player to focus the first element inside the iframe when going backward with the Tab key
    this.onMiniPlayerMouseEnterLeave({ type: 'mouseleave' });
    this._sendMessageToPlayer(PLAYER_EVENTS.FOCUS_LAST_INSIDE_IFRAME);
    e.preventDefault();
  }

  onLastElementKeyDown(e) {
    e.stopPropagation();
    if (!isForwardNav(e)) return;
    // emits an event to the player to focus the last element inside the iframe when going forward with the Tab key
    this.onMiniPlayerMouseEnterLeave({ type: 'mouseleave' });
    this._sendMessageToPlayer(PLAYER_EVENTS.FOCUS_FIRST_INSIDE_IFRAME);
    e.preventDefault();
  }

  focusFirstInteractiveElementInsideIframe() {
    this._sendMessageToPlayer(PLAYER_EVENTS.FOCUS_FIRST_INSIDE_IFRAME);
  }

  createMiniPlayerButton(className, ariaLabel, icon) {
    const button = document.createElement('div');
    button.setAttribute('tabindex', '0');
    button.setAttribute('role', 'button');
    button.setAttribute('aria-label', ariaLabel);
    button.setAttribute('title', ariaLabel);
    button.classList.add(className, 'livecommerce-v2', 'livecommerce-miniplayer-control');
    button.innerHTML = icon;
    return button;
  }

  updateMiniPlayerPlaybackControls() {
    if (!this.miniPlayer) return;
    if (!this.miniPlayer.playPauseButton || !this.miniPlayer.soundButton) return;

    const controls = getMiniPlayerPlaybackControls({
      playbackStatus: this.miniPlayer.playbackStatus,
      theme: this._playerTheme,
    });

    const updateButton = (button, control) => {
      button.hidden = !control.visible;
      button.setAttribute('aria-hidden', control.visible ? 'false' : 'true');
      button.setAttribute('tabindex', control.visible ? '0' : '-1');
      button.setAttribute('aria-label', control.ariaLabel);
      button.setAttribute('title', control.ariaLabel);
      button.setAttribute('aria-pressed', control.pressed ? 'true' : 'false');
      if (button.dataset.action !== control.action) {
        button.dataset.action = control.action;
        button.innerHTML = control.icon;
      }
    };

    updateButton(this.miniPlayer.playPauseButton, controls.playPause);
    updateButton(this.miniPlayer.soundButton, controls.sound);
  }

  setMiniPlayerInitialPosition() {
    const { edgeSpacing } = this.config;
    switch (this.config.minimizedPosition) {
      case MINIMIZED_POSITION.TOP_LEFT:
        this.player.container.style.top = `${edgeSpacing.top}px`;
        this.player.container.style.left = `${edgeSpacing.left}px`;
        break;
      case MINIMIZED_POSITION.TOP_RIGHT:
        this.player.container.style.top = `${edgeSpacing.top}px`;
        this.player.container.style.right = `${edgeSpacing.right}px`;
        break;
      case MINIMIZED_POSITION.BOTTOM_LEFT:
        this.player.container.style.bottom = `${edgeSpacing.bottom}px`;
        this.player.container.style.left = `${edgeSpacing.left}px`;
        break;
      case MINIMIZED_POSITION.BOTTOM_RIGHT:
      default:
        this.player.container.style.bottom = `${edgeSpacing.bottom}px`;
        this.player.container.style.right = `${edgeSpacing.right}px`;
        break;
    }
  }

  unminimize() {
    this.__restoreMinimized && this.__restoreMinimized();
  }

  minimize(targetUrl) {
    this.logger.log('minimize()');

    if (!this.playerSettings.withMinimizeSupport) {
      throw new BambuserLiveShoppingError("Can't minimize player, not supported!");
    }

    // If targetUrl is provided, then we should surf to that URL in the surf behind container.
    if (targetUrl) {
      try {
        this.surfTo(targetUrl);
      } catch (error) {
        this.logger.log('Failed to surf to provided url', { error });
      }
    }

    exitFullscreen();

    this._trackOnInteraction('minimize');
    this._sendMessageToPlayer(PLAYER_EVENTS.SET_VIEWPORT_STATE, { minimized: true });

    const { surfBehindMode } = this.config;
    if (surfBehindMode !== MANUAL) {
      this.showSurf();
      this._disableParentScroll();
    } else {
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport && viewport.getAttribute('content').toLowerCase().indexOf('minimum-scale') === -1)
        // Fix mini-player position/scroll bug on chrome mobile
        viewport.setAttribute('content', `${viewport.getAttribute('content')}, minimum-scale=1`);
      this._restoreParentScroll();
    }

    this.player.container.setAttribute('aria-modal', 'false');
    if (this.player.frame) {
      this.__playerFrameTabIndex = this.player.frame.getAttribute('tabindex');
      this.player.frame.setAttribute('tabindex', '-1');
    }

    const ariaLabels = {
      close: this._getTranslation('closePlayerAriaLabel', 'Close live curation'),
      expand: this._getTranslation('expandPlayerAriaLabel', 'Expand to full player view'),
    };

    const closeButton = document.createElement('div');
    closeButton.setAttribute('tabindex', '0');
    closeButton.setAttribute('role', 'button');
    closeButton.setAttribute('aria-label', ariaLabels.close);
    closeButton.classList.add('livecommerce-close-minimized');
    closeButton.classList.add('livecommerce-v2');
    closeButton.innerHTML = BambuserLiveShopping.closeIconV2;

    // Add expand button (top right aligned)
    const expandButton = document.createElement('div');
    expandButton.setAttribute('tabindex', '0');
    expandButton.setAttribute('role', 'button');
    expandButton.setAttribute('aria-label', ariaLabels.expand);
    expandButton.classList.add('livecommerce-expand-minimized');
    // Using the restore icon for the expand button
    expandButton.innerHTML = BambuserLiveShopping.restoreIcon;
    expandButton.classList.add('livecommerce-v2');
    if (!this.isDesktop) {
      expandButton.style.transition = `opacity ${MINIMIZE_TRANSITION_TIME}ms`;
    }

    this.player.container.appendChild(closeButton);
    this.miniPlayer.closeButton = closeButton;

    this.player.container.appendChild(expandButton);
    this.miniPlayer.expandButton = expandButton;

    const controls = getMiniPlayerPlaybackControls({
      playbackStatus: this.miniPlayer.playbackStatus,
      theme: this._playerTheme,
    });
    const playPauseButton = this.createMiniPlayerButton(
      'livecommerce-miniplayer-play-pause',
      controls.playPause.ariaLabel,
      controls.playPause.icon,
    );
    const soundButton = this.createMiniPlayerButton(
      'livecommerce-miniplayer-sound',
      controls.sound.ariaLabel,
      controls.sound.icon,
    );
    const playbackControls = document.createElement('div');
    playbackControls.classList.add('livecommerce-miniplayer-playback-controls', 'livecommerce-v2');
    playbackControls.appendChild(playPauseButton);
    playbackControls.appendChild(soundButton);
    this.player.container.appendChild(playbackControls);
    this.miniPlayer.playbackControls = playbackControls;
    this.miniPlayer.playPauseButton = playPauseButton;
    this.miniPlayer.soundButton = soundButton;
    this.updateMiniPlayerPlaybackControls();

    this.setMiniPlayerInitialPosition();

    this.player.container.style.transformOrigin = 'bottom right';
    this.player.container.style.overflow = 'hidden';
    this.player.container.style.boxShadow = '';

    if (this.isDesktop && !this.backgroundContainer) {
      this.backgroundContainer = document.createElement('div');
      this.backgroundContainer.setAttribute('id', this.backgroundContainerId);
      this.backgroundContainer.setAttribute('aria-hidden', 'true');
      this.backgroundContainer.style.zIndex = 2147483647;
      this.backgroundContainer.style.transition = `opacity ${MINIMIZE_TRANSITION_TIME}ms`;
      this.player.container.parentNode.insertBefore(this.backgroundContainer, this.player.container);
    }

    Navigation.enableModeDetection();

    const calculateMinimizedView = () => {
      let videoWidth;
      let videoHeight;
      const videoOrientation = this.video.videoOrientation;

      let minWidth;
      let maxWidthFraction;

      const platform = this.isDesktop ? 'DESKTOP' : 'MOBILE';

      switch (this.config.miniplayerSize) {
        case MINIPLAYER_SIZE.LARGE:
          minWidth = MIN_WIDTH.LARGE[platform];
          maxWidthFraction = MAX_WIDTH_FRACTION.LARGE[platform];
          break;
        case MINIPLAYER_SIZE.SMALL:
        default:
          minWidth = MIN_WIDTH.SMALL[platform];
          maxWidthFraction = MAX_WIDTH_FRACTION.SMALL[platform];
          break;
      }

      if (videoOrientation === VIDEO_ORIENTATION.VERTICAL) {
        videoWidth = Math.min(window.innerWidth * maxWidthFraction, minWidth);
        videoHeight = videoWidth * (16 / 9);
      } else {
        videoHeight = Math.min(window.innerHeight * maxWidthFraction, minWidth);
        videoWidth = videoHeight * (16 / 9);
      }
      const { snapHorizontalThresholdRatio, snapVerticalThresholdRatio } = this.miniPlayer;
      const { edgeSpacing } = this.config;
      const snapLeftThreshold = snapHorizontalThresholdRatio
        ? window.innerWidth * snapHorizontalThresholdRatio - videoWidth / 2
        : edgeSpacing.left;
      const snapTopThreshold = snapVerticalThresholdRatio
        ? window.innerHeight * snapVerticalThresholdRatio - videoHeight / 2
        : edgeSpacing.top;
      const snapBottomThreshold = snapVerticalThresholdRatio
        ? window.innerHeight * snapVerticalThresholdRatio - videoHeight / 2
        : edgeSpacing.bottom;

      let minX, minY, maxX, maxY;

      switch (this.config.minimizedPosition) {
        case MINIMIZED_POSITION.TOP_LEFT:
          minX = -edgeSpacing.left;
          minY = -edgeSpacing.top;
          maxX = window.innerWidth - edgeSpacing.left;
          maxY = window.innerHeight - edgeSpacing.top;
          break;
        case MINIMIZED_POSITION.TOP_RIGHT:
          minX = -(window.innerWidth - videoWidth - edgeSpacing.right);
          minY = -edgeSpacing.top;
          maxX = edgeSpacing.right;
          maxY = window.innerHeight - videoHeight - edgeSpacing.top;
          break;
        case MINIMIZED_POSITION.BOTTOM_LEFT:
          minX = -edgeSpacing.left;
          minY = -(window.innerHeight - videoHeight - edgeSpacing.bottom);
          maxX = window.innerWidth - edgeSpacing.left;
          maxY = edgeSpacing.bottom;
          break;
        case MINIMIZED_POSITION.BOTTOM_RIGHT:
        default:
          minX = -(window.innerWidth - videoWidth - edgeSpacing.right);
          minY = -(window.innerHeight - videoHeight - edgeSpacing.bottom);
          maxX = edgeSpacing.right;
          maxY = edgeSpacing.bottom;
          break;
      }

      this.miniPlayer.snapLeftThreshold = snapLeftThreshold;
      this.miniPlayer.snapTopThreshold = snapTopThreshold;
      this.miniPlayer.snapBottomThreshold = snapBottomThreshold;

      this.miniPlayer.minX = minX;
      this.miniPlayer.minY = minY;
      this.miniPlayer.maxX = maxX;
      this.miniPlayer.maxY = maxY;

      this.miniPlayer.videoWidth = videoWidth;
      this.miniPlayer.videoHeight = videoHeight;

      const ensurePositionInsideViewportBounds = (isDragging = true) => {
        const { position } = this.miniPlayer;

        switch (this.config.minimizedPosition) {
          case MINIMIZED_POSITION.TOP_LEFT:
            if (-position.x > edgeSpacing.left) {
              position.x = minX; // mini-player going out by the left
            } else if (Math.abs(position.x) + videoWidth + edgeSpacing.left > window.innerWidth) {
              position.x = maxX - videoWidth; // mini-player going out by the right
            }

            if (position.y < minY) {
              position.y = isDragging ? minY : 0; // mini-player going out by the top
            } else if (position.y + videoHeight + edgeSpacing.top > window.innerHeight) {
              position.y = maxY - videoHeight; // mini-player going out by the bottom
            }
            break;
          case MINIMIZED_POSITION.TOP_RIGHT:
            if (Math.abs(position.x) + videoWidth + edgeSpacing.right > window.innerWidth) {
              position.x = minX; // mini-player going out by the left
            } else if (position.x > edgeSpacing.right) {
              position.x = maxX; // mini-player going out by the right
            }

            if (position.y < minY) {
              position.y = isDragging ? minY : 0; // mini-player going out by the top
            } else if (position.y > maxY) {
              position.y = maxY; // mini-player going out by the bottom
            }
            break;
          case MINIMIZED_POSITION.BOTTOM_LEFT:
            if (-position.x > edgeSpacing.left) {
              position.x = minX; // mini-player going out by the left
            } else if (Math.abs(position.x) + videoWidth + edgeSpacing.left > window.innerWidth) {
              position.x = maxX - videoWidth; // mini-player going out by the right
            }

            if (Math.abs(position.y) + videoHeight + edgeSpacing.bottom > window.innerHeight) {
              position.y = isDragging ? minY : minY + edgeSpacing.top; // mini-player going out by the top
            } else if (position.y > edgeSpacing.bottom) {
              position.y = maxY; // mini-player going out by the bottom
            }
            break;
          case MINIMIZED_POSITION.BOTTOM_RIGHT:
          default:
            if (Math.abs(position.x) + videoWidth + edgeSpacing.right > window.innerWidth) {
              position.x = minX; // mini-player going out by the left
            } else if (position.x > edgeSpacing.right) {
              position.x = maxX; // mini-player going out by the right
            }

            if (videoHeight + edgeSpacing.bottom > window.innerHeight + position.y) {
              position.y = isDragging ? minY : minY + edgeSpacing.top; // mini-player going out by the top
            } else if (position.y > edgeSpacing.bottom) {
              position.y = maxY; // mini-player going out by the bottom
            }
            break;
        }

        // if the mini-player is in the left part of the screen
        if (!isDragging && position.x < (maxX - minX) / -2) {
          position.x = minX + edgeSpacing.left; // we add the left margin
        }
      };

      ensurePositionInsideViewportBounds(false);

      const playerStyle = this.player.container.style;
      playerStyle.width = `${videoWidth}px`;
      playerStyle.height = `${videoHeight}px`;
      playerStyle.transform = `translate(${this.miniPlayer.position.x}px, ${this.miniPlayer.position.y}px)`;
      playerStyle.borderRadius = '8px';

      this._miniPlayerDragStart = () => {
        if (this.config.surfBehindMode === MANUAL) this._disableParentScroll({ fixPlayerPos: true });
      };

      this._miniPlayerDragEnd = () => {
        this.snapToBorders();
        if (this.config.surfBehindMode === MANUAL) this._restoreParentScroll();
      };

      this.__handleMoveMinimizedPlayer = ({ dx, dy }) => {
        const { requestedPosition, position } = this.miniPlayer;

        // If the positions are not in sync before to move, we reset the requested position
        // This happen when we continue to drag while the player is already touching a page border
        // It avoids to have to wait before to be able to unstick the player from a border
        if (requestedPosition.x !== position.x) {
          requestedPosition.x = position.x;
        }
        if (requestedPosition.y !== position.y) {
          requestedPosition.y = position.y;
        }

        // Storing the requested position in a separate variable and using the actually allowed position in another
        // allows us to stop moving the player outside viewport bounds but to continue moving when finger/mouse returns
        // back to an allowed position. Would we only use one variable that would reset to allowed position it would
        // still work but would offset the finger/mouse from the original dragging point when returning back to allowed
        // dragging area and create a very confusing drag UX.
        requestedPosition.x += dx;
        requestedPosition.y += dy;
        position.x = requestedPosition.x;
        position.y = requestedPosition.y;

        ensurePositionInsideViewportBounds(true);
        this.moveMiniPlayer();

        if (!this.quickMove.enabled) {
          return;
        }

        // Quick move in progress
        if (this.quickMove.orientation) {
          // While there is quick moves detected (+50ms margin), we look for the move with the biggest trust index:
          // We track the biggest value abs(dx)+abs(dy) in order to have a better detection of the expected orientation
          const trustIndex = Math.abs(dx) + Math.abs(dy);
          if (trustIndex <= this.quickMove.trustIndex) {
            return;
          }
          this.quickMove.trustIndex = trustIndex;
        }

        this.quickMove.orientation = this.getQuickMoveOrientation(dx, dy);

        if (this.quickMove.orientation) {
          this.logger.log('Quick move detected', dx, dy);
          this.quickMove.trustIndex = Math.abs(dx) + Math.abs(dy);
          this.quickMove.detectionCount++;
          this.quickMove.targetedPosition = this.getQuickMoveTargetedPosition(
            this.quickMove.orientation,
            requestedPosition,
          );

          const quickMove = () => {
            // False positive detection
            if (this.quickMove.detectionCount < QUICK_MOVE.MIN_COUNT_THRESHOLD) {
              this.logger.log('Quick move false positive detected');
              this.quickMoveReset();
              return;
            }

            const { x, y } = this.quickMove.targetedPosition;
            this.logger.log('Quick move action', dx, dy);
            position.x = x;
            position.y = y;
            ensurePositionInsideViewportBounds(true);
            this.moveMiniPlayer();
            this.quickMoveReset();
          };
          quickMove.bind(this);

          if (this.quickMove.timeout) {
            window.clearTimeout(this.quickMove.timeout);
          }
          this.quickMove.timeout = window.setTimeout(quickMove, QUICK_MOVE.DETECTION_DURATION);
        }
      };
    };

    this.__resetMinimized = () => {
      this.logger.log('__resetMinimized()');
      // Reset minimized styles (except the transition property so it can animate back nicely).
      if (!this.isDesktop) {
        this.player.container.style.transition = `transform ${MINIMIZE_TRANSITION_TIME}ms`;
      }

      this.player.container.style.top = '';
      this.player.container.style.left = '';
      this.player.container.style.bottom = 0;
      this.player.container.style.right = 0;

      this.player.container.style.width = '100%';
      this.player.container.style.height = '100%';
      this.player.container.style.transform = '';
      this.player.container.style.borderRadius = '';
      this.player.container.style.boxShadow = '';
      this.player.container.style.overflow = '';
      this.player.container.setAttribute('aria-modal', 'true');
      if (this.isDesktop) {
        this.backgroundContainer.style.display = '';
        this.backgroundContainer.style.opacity = 1;
      }
      this.player.container.removeChild(this.miniPlayer.closeButton);
      this.player.container.removeChild(this.miniPlayer.expandButton);
      if (this._pointerFocusCleanup) {
        this._pointerFocusCleanup();
        delete this._pointerFocusCleanup;
      }
      if (typeof this.miniPlayer.removeControlListeners === 'function') {
        this.miniPlayer.removeControlListeners();
        delete this.miniPlayer.removeControlListeners;
      }
      if (this.miniPlayer.playbackControls) {
        this.player.container.removeChild(this.miniPlayer.playbackControls);
        delete this.miniPlayer.playbackControls;
      }
      delete this.miniPlayer.playPauseButton;
      delete this.miniPlayer.soundButton;
      if (typeof this.__playerFrameTabIndex !== 'undefined' && this.player.frame) {
        if (this.__playerFrameTabIndex === null) {
          this.player.frame.removeAttribute('tabindex');
        } else {
          this.player.frame.setAttribute('tabindex', this.__playerFrameTabIndex);
        }
        delete this.__playerFrameTabIndex;
      }
      this._disableParentScroll();
      delete this.__handleMoveMinimizedPlayer;
      window.removeEventListener('resize', this.__calculateMinimizedView);
      if (this.isDesktop) {
        this.player.container.removeEventListener('mouseenter', this.onMiniPlayerMouseEnterLeave);
        this.player.container.removeEventListener('mouseleave', this.onMiniPlayerMouseEnterLeave);
      }

      delete this.__calculateMinimizedView;
      delete this.__resetMinimized;
      delete this.__restoreMinimized;
      delete this.__closeMinimized;
    };

    const resetMinimized = this.__resetMinimized;

    this.__restoreMinimized = () => {
      this.logger.log('__restoreMinimized()');
      this._trackOnInteraction('restore');
      Navigation.disableModeDetection();
      this._sendMessageToPlayer(PLAYER_EVENTS.SET_VIEWPORT_STATE, {
        minimized: false,
        mouseIsOver: false,
      });

      // TODO: Investigate if we can solve this problem in some other way. Ex. is it viable that merchant would emit
      // some event with postMessage that we can pick up from the surf iframe?
      this.syncCartState();

      resetMinimized();

      if (!this.surf.frame && !this.activePlayerStyleElem.parentNode) {
        document.head.appendChild(this.activePlayerStyleElem);
      }

      // Try ensure site doesn't have any other active playbacks
      // as show should have focus again when restored from minimized mode.
      this._pauseOtherPlaybacks();

      delete this.__restoreMinimized;
      this._isMinimized = false;
    };

    this.__closeMinimized = () => {
      this.close({ origin: 'miniplayer' });
    };

    const onCloseClick = () => this.__closeMinimized();
    const onCloseKeyDown = (e) => this._onActionKeyDown(e, this.__closeMinimized);
    const onExpandClick = () => this.__restoreMinimized();
    const onExpandKeyDown = (e) => this._onActionKeyDown(e, this.__restoreMinimized);
    let removePlayPauseListeners;
    let removeSoundListeners;

    this.miniPlayer.closeButton.addEventListener('click', onCloseClick);
    this.miniPlayer.closeButton.addEventListener('keydown', onCloseKeyDown);
    this.miniPlayer.expandButton.addEventListener('click', onExpandClick);
    this.miniPlayer.expandButton.addEventListener('keydown', onExpandKeyDown);

    if (this.miniPlayer.playPauseButton) {
      const togglePlayPause = () => {
        this.miniPlayer.playPauseButton.dataset.action === 'pause' ? this.pause() : this.play();
      };
      const onPlayPauseKeyDown = (e) => this._onActionKeyDown(e, togglePlayPause);
      this.miniPlayer.playPauseButton.addEventListener('click', togglePlayPause);
      this.miniPlayer.playPauseButton.addEventListener('keydown', onPlayPauseKeyDown);
      removePlayPauseListeners = () => {
        this.miniPlayer.playPauseButton.removeEventListener('click', togglePlayPause);
        this.miniPlayer.playPauseButton.removeEventListener('keydown', onPlayPauseKeyDown);
      };
    }

    if (this.miniPlayer.soundButton) {
      const toggleSound = () => {
        this.miniPlayer.soundButton.dataset.action === 'unmute' ? this.unmute() : this.mute();
      };
      const onSoundKeyDown = (e) => this._onActionKeyDown(e, toggleSound);
      this.miniPlayer.soundButton.addEventListener('click', toggleSound);
      this.miniPlayer.soundButton.addEventListener('keydown', onSoundKeyDown);
      removeSoundListeners = () => {
        this.miniPlayer.soundButton.removeEventListener('click', toggleSound);
        this.miniPlayer.soundButton.removeEventListener('keydown', onSoundKeyDown);
      };
    }

    this.miniPlayer.removeControlListeners = () => {
      this.miniPlayer.closeButton.removeEventListener('click', onCloseClick);
      this.miniPlayer.closeButton.removeEventListener('keydown', onCloseKeyDown);
      this.miniPlayer.expandButton.removeEventListener('click', onExpandClick);
      this.miniPlayer.expandButton.removeEventListener('keydown', onExpandKeyDown);
      removePlayPauseListeners && removePlayPauseListeners();
      removeSoundListeners && removeSoundListeners();
    };

    // Post animation steps
    // * Restore button didn't look nice during animation (very large->small, but maybe it can be fixed somehow).
    // * Animating without a box shadow creates a much smoother animation - add the box shadow afterwards.
    // * Prepare for receiving resize events and recalculate minimzed view without any animations.

    // Check if device have supports touch input. Don't hide restore/close controls
    // which is hides behind the hover effect if device supports touch.
    const supportsTouch = 'ontouchstart' in window || window.navigator.msMaxTouchPoints;

    window.setTimeout(() => {
      if (this.isDesktop && !supportsTouch) {
        this.player.container.addEventListener('mouseenter', this.onMiniPlayerMouseEnterLeave);
        this.player.container.addEventListener('mouseleave', this.onMiniPlayerMouseEnterLeave);
      } else {
        this.miniPlayer.closeButton.style.display = 'flex';
        this.miniPlayer.closeButton.style.opacity = 1;
      }
      this.player.container.style.transition = '';
      calculateMinimizedView();
    }, MINIMIZE_TRANSITION_TIME);

    this.__calculateMinimizedView = calculateMinimizedView.bind(this);
    window.addEventListener('resize', this.__calculateMinimizedView);

    if (this.isDesktop) {
      // Next frame please...
      window.setTimeout(() => {
        this.backgroundContainer.style.opacity = 0;
        this.player.container.style.backgroundColor = 'transparent';
      }, 0);
    } else {
      const playerStyle = this.player.container.style;
      playerStyle.transition = `transform ${MINIMIZE_TRANSITION_TIME}ms, border-radius ${MINIMIZE_TRANSITION_TIME}ms`;
    }

    this.__calculateMinimizedView();

    if (!this.surf.frame && this.activePlayerStyleElem.parentNode) {
      this.activePlayerStyleElem.parentNode.removeChild(this.activePlayerStyleElem);
    }

    this._isMinimized = true;
  }

  async enterPlayerFullscreen() {
    if (!this.player.container) return;
    try {
      await enterFullscreen(this.player.container);
    } catch (error) {
      console.log(error);
    }
  }

  async exitPlayerFullscreen() {
    try {
      await exitFullscreen();
    } catch (error) {
      console.log(error);
    }
  }

  listenToPlayerFullscreenEvents() {
    if (!this.player.container) return;
    return listenToFullscreenEvents(this.player.container, () => {
      this._sendMessageToPlayer(PLAYER_EVENTS.SET_VIEWPORT_STATE, { fullscreen: isFullscreen() });
    });
  }

  surfTo(url) {
    this.logger.log('surfTo(url)', url);
    if (!this.playerSettings.withMinimizeSupport) {
      throw new BambuserLiveShoppingError("Can't surf to url behind player, not supported!");
    }

    const { surfBehindMode } = this.config;

    if (surfBehindMode === MANUAL) {
      if (url !== window.location.href) {
        this._emitEvent(PLAYER_EVENTS_PUBLIC.NAVIGATE_BEHIND_TO, { url });
        this._emitEvent('surf-behind-to', { url });
      }
      return;
    }

    const surfFrame = this.getSurfFrame();
    surfFrame.setAttribute('src', url);

    this._emitEvent(PLAYER_EVENTS_PUBLIC.NOTIFY_URL_CHANGE, { url });

    // Try ensure site doesn't have any other active playbacks
    // as it will be kinda impossible to pause them with the surf iframe above otherwise.
    this._pauseOtherPlaybacks();
  }

  // TODO: Copy scrollbar fixes from Live Meetings surf behind popup impl.
  showSurf() {
    this.logger.log('showSurf()');
    this.surf.container.style.visibility = 'visible';
    this.surf.container.style.pointerEvents = 'auto';
    this._disableParentScroll();
    // Ensure any scrollbars are hidden on desktop browsers (mostly linux?)
    if (this.isDesktop) {
      document.documentElement.style.overflow = 'hidden';
    }
    // Ensure all main elements on site is either hidden/visible when iframe is visible/hidden.
    // We do this because it doesn't seem that on iOS body overflow: hidden works and we don't want to
    // be able to scroll into the underlying page (that the iframe should cover).
    // Also it may improve performance to hide all elements that is hidden anyway...
    // TODO: Create a style node which writes this and uses "!important" instead so we can be totally sure.
    const notSurfContainer = `:not(#${this.surf.containerId})`;
    const notPlayerContainer = `:not(#${this.player.containerId})`;
    const notBackgroundContainer = `:not(#${this.backgroundContainerId})`;
    const querySelector = `body > *${notSurfContainer}${notPlayerContainer}${notBackgroundContainer}`;
    const elements = document.querySelectorAll(querySelector);

    const playerContainerNodeTopParent = getNodeTopParent(this.config.playerContainerNode);
    // eslint-disable-next-line
    for (const element of elements) {
      if (element === playerContainerNodeTopParent) continue;
      const styles = window.getComputedStyle && window.getComputedStyle(element);
      const isHidden = (styles && styles.display === 'none') || element.style.display === 'none';
      if (!isHidden) {
        element.setAttribute('data-surf-behind-hidden', '1');
        element.style.display = 'none';
      }
    }

    // REMARK: Fix some weird rendering issues with iOS and elements with fixed positioning inside the surf iframe.
    // By specifying this styling value on page loads it can later be removed or changed by the page inside the iframe
    // and everything would work as expected, without this the UI could potentially be unusable other wise...
    if (isAppleMobileDevice() || isMacWithMultiTouch()) {
      window.setTimeout(() => {
        try {
          const { frame: surfFrame } = this.surf;
          const surfDocument = surfFrame.contentDocument || surfFrame.contentWindow.document;
          if (surfDocument?.body.style.position) {
            surfDocument.body.style.position = 'unset';
          }
          surfDocument.body.style.position = 'initial';
        } catch (e) {
          /* Just swallow any cross-domain errors... */
        }
      }, 100);
    }
  }

  hideSurf() {
    this.logger.log('hideSurf()');
    if (this.historyPollingInterval) {
      window.clearInterval(this.historyPollingInterval);
      this.historyPollingInterval = null;
    }
    this.surf.container.style.visibility = 'hidden';
    this.surf.container.style.pointerEvents = 'none';
    this._restoreParentScroll();
    if (this.isDesktop) {
      document.documentElement.style.overflow = '';
    }
    const elements = document.querySelectorAll('[data-surf-behind-hidden]');
    // eslint-disable-next-line
    for (const element of elements) {
      element.removeAttribute('data-surf-behind-hidden');
      element.style.display = '';
    }
  }

  onSurfLoad() {
    let surfDocument;
    try {
      const { frame: surfFrame, skipNextBrowserHistoryEntry } = this.surf;
      const surfWindow = surfFrame.contentWindow;
      surfDocument = surfFrame.contentDocument || surfFrame.contentWindow.document;
      const { surfBehindMode } = this.config;
      const surfHref = surfWindow.location.href;
      const url = surfHref;

      // prettier-ignore
      this.logger.log(
        'onSurfLoad() config.surfBehindMode', surfBehindMode,
        'surfHref', surfHref, // cross domain issue
        'surfDocument.baseURI', surfDocument.baseURI, // cross domain issue
        'skipNextBrowserHistoryEntry', skipNextBrowserHistoryEntry,
        'url', url,
      );

      if (
        surfBehindMode !== MANUAL &&
        url !== 'about:blank'
      ) {
        this._pollHistoryFromSurfIframe();
        // Start history polling
        if (!this.historyPollingInterval) {
          this.historyPollingInterval = window.setInterval(() => this._pollHistoryFromSurfIframe(), 500); // Check every 500ms
        }
      } else {
        this.logger.log('NO history.replaceState surfBehindMode', surfBehindMode, 'url', url);
      }
    } catch (e) {
      console.warn('Unable to sync browser url', e.message);
    }

    this.surf.skipNextBrowserHistoryEntry = false; // reset the value

    // REMARK: Fix some weird rendering issues with iOS and elements with fixed positioning inside the surf iframe.
    // By specifying this styling value on page loads it can later be removed or changed by the page inside the iframe
    // and everything would work as expected, without this the UI could potentially be unusable other wise...
    if (isAppleMobileDevice() || isMacWithMultiTouch()) {
      try {
        if (!surfDocument?.body.style.position) {
          surfDocument.body.style.position = 'initial';
        }
      } catch (e) {
        /* Just swallow any cross-domain errors... */
      }
    }
  }

  onPopstate(event) {
    // prettier-ignore
    this.logger.log(
      'popstate(event) event.state', event.state,
      'surfBehindMode', this.config.surfBehindMode,
      'state.bambuserLiveShopping', event.state && event.state.bambuserLiveShopping,
      'state.___IFRAME_DEPTH_BRIDGE___', event.state && event.state.___IFRAME_DEPTH_BRIDGE___,
    );

    if (isPlayerHistoryBridgeState(event.state) && !event.state.bambuserLiveShopping) return;

    if (event.state && event.state.bambuserLiveShopping) {
      this.showSurf();
    } else {
      // We are back on the landing page (where the player is minified for the 1st time)
      document.title = this.landingPagePageTitle; // we restore the landing page title
      this.hideSurf();
    }
  }

  initializeSurf() {
    this.logger.log('initializeSurf()');

    window.addEventListener('popstate', this.onPopstate.bind(this));

    const surfFrame = document.createElement('iframe');
    surfFrame.style.width = '100%';
    surfFrame.style.height = '100%';
    surfFrame.setAttribute('frameborder', '0');
    surfFrame.setAttribute('title', 'Surf behind live video shopping');
    if (this.config.sandboxAttributes) surfFrame.setAttribute('sandbox', this.config.sandboxAttributes);
    if (this.config.credentiallessIframes) surfFrame.setAttribute('credentialless', true);
    surfFrame.onload = this.onSurfLoad.bind(this);
    this.surf.frame = surfFrame;
    Navigation.setSurfFrame(surfFrame);

    const surfContainer = document.createElement('div');
    surfContainer.style.visibility = 'hidden';
    surfContainer.style.pointerEvents = 'none';
    surfContainer.style.position = 'fixed';
    surfContainer.style.zIndex = 2147483647;
    surfContainer.style.top = 0;
    surfContainer.style.left = 0;
    surfContainer.style.bottom = 0;
    surfContainer.style.right = 0;
    surfContainer.style.width = '100%';
    surfContainer.style.height = '100%';
    surfContainer.style.overscrollBehavior = 'inherit';
    surfContainer.setAttribute('id', this.surf.containerId);
    surfContainer.appendChild(surfFrame);
    this.surf.container = surfContainer;

    this.config.type === 'inline'
      ? document.body.appendChild(surfContainer)
      : this.player.container.parentNode.insertBefore(surfContainer, this.player.container);

    // Setup postMessage listener for surf iframe
    this.__receiveSurfFrameMessage = this._receiveSurfFrameMessage.bind(this);
    PostMessageConduit.registerIframeListener(surfFrame, this.__receiveSurfFrameMessage);
  }

  showSurfTimeout() {
    this.logger.log('popstate timeout showSurf(true)');
    this.showSurf();
  }

  getSurfFrame() {
    this.logger.log('getSurfFrame()');
    if (!this.surf.frame) {
      this.initializeSurf();
    }
    return this.surf.frame;
  }

  showCheckout(url) {
    if (this.playerSettings.preferNewTabCheckout) {
      document.addEventListener(
        'visibilitychange',
        (this.__onReturnToWindow = () => {
          if (!document.hidden) {
            document.removeEventListener('visibilitychange', this.__onReturnToWindow);
            delete this.__onReturnToWindow;
            this.syncCartState();
          }
        }).bind(this),
      );
      window.open(url, '_blank');
    } else {
      this.surfTo(url);
      this.minimize();
    }
  }

  /**
   * Specifies if tracking cookies should be updated
   * @returns {boolean}
   */
  get shouldUpdateTrackingCookies() {
    return this.config.enableFirstPartyCookies && this._trackingContext;
  }

  /**
   * Returns device type for current device
   * @returns {'desktop' | 'mobile'}
   */
  get deviceType() {
    return this.isDesktop ? 'desktop' : 'mobile';
  }

  /**
   * Get version of the player
   * @return {number} player version
   */
  get playerVersion() {
    const v2 = urlParams.get('v2');
    if (v2 === '1' || v2 === 'true') return 2;
    if (v2 === '0' || v2 === 'false') return 1;
    if (window.__playerVersion === 2) return 2;
    if (this.playerSettings?.playerVersion) return this.playerSettings.playerVersion;
    return null; // Unless forced via query parameter, the player itself will insist on a version
  }

  /**
   * Get if player is in dashboard
   * @return {number} is dashboard
   */
  get isDashboard() {
    return window.__isDashboard === 1 ? 1 : null;
  }

  /**
   * Update tracking with custom tracking tags
   * @param {array} trackingTags - array of tracking tags [{key: 'myTag', value: 'myValue'}]
   */
  setTrackingTags(trackingTags = []) {
    if (!Array.isArray(trackingTags)) {
      throw new BambuserLiveShoppingError('Tracking tags must be an array of key value pairs');
    }
    if (trackingTags.length > 20) {
      console.warn('Bambuser limits number of tracking tags to 20. Excess tags will be ignored');
      trackingTags = trackingTags.slice(0, 20);
    }
    // Validate tags, remove invalid ones and duplicates
    trackingTags = Array.from(
      trackingTags.reduce((map, tag) => {
        const { key, value } = tag;
        if (!key || typeof key !== 'string') {
          console.warn('Bambuser rejected tracking tag with invalid or missing key', tag);
          return map;
        }
        // Only allow string, number or boolean values
        if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
          console.warn('Bambuser rejected tracking tag with invalid or missing value', tag);
          return map;
        }
        if (typeof value === 'string' && new Blob([value]).size > 1024) {
          console.warn('Bambuser rejected tracking tag exceeding allowed size limit of 1KB', tag);
          return map;
        }
        // Make sure there are no duplicates
        return map.set(key, {
          key,
          value,
        });
      }, new Map()).values(),
    );
    this._sendMessageToPlayer(PLAYER_EVENTS.TRACKING_TAGS, {
      trackingTags,
    });
    this.trackingTags = trackingTags;
  }

  /**
   * Update products with fresh data
   * @param {[object|SchemaModel]} products
   */
  updateProducts(products) {
    const validProducts = [];
    // eslint-disable-next-line
    for (const product of products) {
      if (product instanceof SchemaModel) {
        try {
          validProducts.push(product.toObject());
        } catch (e) {
          console.info(`Ignoring invalid product: ${e.message}`);
        }
      } else {
        validProducts.push(product);
      }
    }

    if (this.debug) {
      if (!window._bambuserHydratedProductsDebugStorage) window._bambuserHydratedProductsDebugStorage = {};
      for (const product of validProducts) {
        window._bambuserHydratedProductsDebugStorage[product._id] = product;
      }
    }

    this._sendMessageToPlayer(PLAYER_EVENTS.PROVIDE_PRODUCT_DATA, { products: validProducts });
    this.playerDelegate.pushEvent('internal-hydrated-product-update', {
      data: {
        products: validProducts,
      },
    });
  }

  /**
   * Hide all user interfaces from the player
   */
  hideUI(uiSections) {
    this._sendMessageToPlayer(PLAYER_EVENTS.HIDE_UI, { uiSections });
  }

  /**
   * Show all user interfaces of the player
   */
  showUI() {
    this._sendMessageToPlayer(PLAYER_EVENTS.SHOW_UI);
  }

  /**
   * Tell player timeline to play
   */
  play() {
    this._sendMessageToPlayer(PLAYER_EVENTS.PLAY);
  }

  /**
   * Tell player timeline to pause
   */
  pause() {
    this._sendMessageToPlayer(PLAYER_EVENTS.PAUSE);
  }

  /**
   * Tell player to show product list
   */
  showProductList() {
    this._sendMessageToPlayer(PLAYER_EVENTS.SHOW_PRODUCT_LIST);
  }

  /**
   * Tell player to show product view
   */
  showProductView({ id, stallUntilHydration }) {
    this._sendMessageToPlayer(PLAYER_EVENTS.SHOW_PRODUCT_VIEW, { id, stallUntilHydration });
  }

  /**
   * Tell player to hide product list
   */
  hideProductList() {
    this._sendMessageToPlayer(PLAYER_EVENTS.HIDE_PRODUCT_LIST);
  }

  playerApiCall(eventName, data = {}, waitForResponse = false) {
    return new Promise((resolve, reject) => {
      if (!eventName) {
        return reject(new Error('eventName required'));
      }
      if (waitForResponse) {
        const responseId = `response-${eventName}:${Date.now()}`;
        if (!data) data = {};
        data.responseId = responseId;
        this._pendingRequestsToPlayer[responseId] = (response, error) => {
          if (error) {
            reject(error);
          } else {
            resolve(response);
          }
        };
      } else {
        resolve(null);
      }
      this._sendMessageToPlayer(eventName, data);
    });
  }

  /**
   * Tell player timeline to seek to percentange
   */
  seekToPercent(seekToPercentage) {
    this._sendMessageToPlayer(PLAYER_EVENTS.SEEK_TO_PERCENT, { seekToPercentage });
  }

  /**
   * Tell player to initiate shutdown sequence
   */
  close(opts = {}) {
    if (!this._playerReady) {
      // Tear down player immediately if it is not ready yet
      this.logger.log('close() player not ready, destroying immediately');
      this.destroy();
    } else {
      this._sendMessageToPlayer(PLAYER_EVENTS.CLOSE, opts);
    }
  }

  /**
   * Tell player timeline to mute
   */
  mute() {
    this._sendMessageToPlayer(PLAYER_EVENTS.MUTE);
  }

  /**
   * Tell player timeline to unmute
   */
  unmute() {
    this._sendMessageToPlayer(PLAYER_EVENTS.UNMUTE);
  }

  /**
   * Tell player to show captions
   * @return {Promise} Resolves with result from player
   */
  showCaptions() {
    return this.playerApiCall(PLAYER_EVENTS.SHOW_CAPTIONS, null, true);
  }

  /**
   * Tell player to hide captions
   * @return {Promise} Resolves with result from player
   */
  hideCaptions() {
    return this.playerApiCall(PLAYER_EVENTS.HIDE_CAPTIONS, null, true);
  }

  /**
   * Tell player to enable high-contrast styling
   */
  enableHighContrast() {
    this._sendMessageToPlayer(PLAYER_EVENTS.ENABLE_HIGH_CONTRAST);
  }

  /**
   * Tell player to disable high-contrast styling. OS-level
   * prefers-contrast: more still applies.
   */
  disableHighContrast() {
    this._sendMessageToPlayer(PLAYER_EVENTS.DISABLE_HIGH_CONTRAST);
  }

  /**
   * Tell player to select a specific caption track
   * @param {string} languageCode - The language code of the caption track to select
   * @return {Promise} Resolves with result from player
   */
  selectCaptionTrack(languageCode) {
    return this.playerApiCall(PLAYER_EVENTS.SELECT_CAPTION_TRACK, { languageCode }, true);
  }

  /**
   * Get available caption tracks from player
   * @return {Promise<object>} Resolves with available captions info
   */
  getAvailableCaptions() {
    return this.playerApiCall(PLAYER_EVENTS.GET_AVAILABLE_CAPTIONS, null, true);
  }

  /**
   * Patch theme overrides into the player. Accumulates across calls via deep
   * merge, so partial updates compose. Safe to call before the player is
   * ready — the cumulative state is queued and flushed on player-ready.
   * @param {object} themeOverrides - shape mirrors the playerSettings tree
   *   under the generalV2 theme, e.g.
   *   { playerSettings: { closedCaptions: { foregroundColor, backgroundColor } } }
   *   The generalV2 wrapping is added internally before forwarding to the player.
   */
  patchTheme(themeOverrides) {
    if (!themeOverrides || typeof themeOverrides !== 'object' || Array.isArray(themeOverrides)) return;
    this._cumulativeThemeOverrides = deepMergeObjects(
      { ...(this._cumulativeThemeOverrides || {}) },
      themeOverrides,
    );
    this._sendMessageToPlayer(PLAYER_EVENTS.APPLY_THEME, {
      theme: { generalV2: this._cumulativeThemeOverrides },
    });
  }

  /**
   * Factory for product generator
   * - Creates a new product instance
   * @param {string} productId
   * @return {LocalizedProduct}
   */
  createProduct(productId) {
    return CreateProduct(productId);
  }

  unloadPlayerApp() {
    // Instruct player React app to unload itself
    try {
      // This event is picked up by a message handler in the root HTML page of the player
      // which does not have any JSON wrapping around the message.
      this.player.frame.contentWindow.postMessage('unloadReactApp', '*');
    } catch (e) {
      // Nuclear option if postMessage fails for some reason
      this.destroy();
    }

  }

  destroy() {
    this.logger.log('destroy()');

    // Cancel any pending forced destroy timeout
    window.clearTimeout(this._forceDestroyPlayerTimeoutHandle);
    
    if (this.historyPollingInterval) {
      window.clearInterval(this.historyPollingInterval);
      this.historyPollingInterval = null;
    }

    this._playerReady = false;

    // Reset the webpage's virewport content to what it was (hopefully) before the player was opened
    this._resetBackToPreviousViewportContent();

    // TODO: remove autoplayLiveShopping from URL, user doesn't want this param property anymore
    // const urlParams = new URLSearchParams(window.location.search);
    // if (urlParams.has('autoplayLiveShopping')) {}

    if (this.__resetMinimized) {
      this.__resetMinimized(true);
      delete this.__resetMinimized;
    }

    if (this._pointerFocusCleanup) {
      this._pointerFocusCleanup();
      delete this._pointerFocusCleanup;
    }

    if (this.__restoreMinimized) {
      delete this.__restoreMinimized;
    }

    if (this.__closeMinimized) {
      delete this.__closeMinimized;
    }

    if (this.__calculateMinimizedView) {
      delete this.__calculateMinimizedView;
    }

    if (this.__cancelFullscreenListener) {
      this.__cancelFullscreenListener();
      delete this.__cancelFullscreenListener;
    }

    this._frames.forEach((frame) => {
      PostMessageConduit.unregisterIframeListener(frame, this._processReceivedMessage);
    });
    this._frames = [];

    this._destroyHtmlNodes();

    if (this.__processAppDevEnvMessage) {
      PostMessageConduit.unregisterGlobalListener(this.__processAppDevEnvMessage);
      delete this.__processAppDevEnvMessage;
    }

    if (this.__receiveMessageForInstanceKeyCallback) {
      window.removeEventListener('message', this.__receiveMessageForInstanceKeyCallback, false);
      delete this.__receiveMessageForInstanceKeyCallback;
    }

    // REMARK: Surf frame message listener is not removed by choice.
    // As the surf frame is kept to allow surfing after closing a show, we may still need to open
    // new shows via this embed instance.
    // if (this.__receiveSurfFrameMessage) {}

    if (this.__preventBodyScroll) {
      document.removeEventListener('touchmove', this.__preventBodyScroll, { passive: false });
      delete this.__preventBodyScroll;
      this._restoreParentScroll();
    }

    if (this.__pushPageScrollTopHandler) {
      window.removeEventListener('scroll', this.__pushPageScrollTopHandler);
      delete this.__pushPageScrollTopHandler;
      if (this.__pushPageScrollTopTimeout) {
        window.clearTimeout(this.__pushPageScrollTopTimeout);
        delete this.__pushPageScrollTopTimeout;
      }
    }

    this._clearCookieRefreshInterval();
    if (this._trackingContext?.showWasPlayed) {
      this._updateTrackingCookies();
    }

    if (window.__currentBambuserLiveShoppingOverlayPlayer === this) {
      delete window.__currentBambuserLiveShoppingOverlayPlayer;
    }
    if (this.config._useExternalPlayerController) {
      this.playerDelegate.resetPlayerControllers();
    }

    for (const [ pendingRequestKey, pendingRequest ] of Object.entries(this._pendingRequestsToPlayer)) {
      if (typeof pendingRequest === 'function') pendingRequest(undefined, 'The request is rejected due to the player being closed.');
      delete this._pendingRequestsToPlayer[pendingRequestKey];
    }

    BrowserHistoryManager.removeEventListener('goBack', this._onGoBack);

    this._didLoadPlayer = false;
    this.hasBeenShown = false;
  }

  updateTrackingContextData(newTrackingContextData = {}) {
    if (!this.shouldUpdateTrackingCookies) return;
    Object.assign(this._trackingContext, newTrackingContextData);
    this._updateTrackingCookies();
  }

  _getPlayerUrl() {
    return generatePlayerIframeUrlForEmbedInstance(this);
  }

  _destroyHtmlNodes() {
    this.logger.log('_destroyHtmlNodes()');

    if (this.player.container) {
      this.hideLoader();
      if (!this.config.containerNode) {
        this.player.container.parentNode.removeChild(this.player.container);
        this.player.container = null;
        this.player.frame = null;
        this.loader = null;
        delete this.player.containerId;
      }
    }

    if (this.backgroundContainer) {
      this.backgroundContainer.parentNode.removeChild(this.backgroundContainer);
      this.backgroundContainer = null;
      delete this.backgroundContainerId;
    }

    // REMARK: Surf container is not removed by choice.
    // The surf frame needs to be kept to allow surfing after closing a show.
    // if (this.surf.container) {}

    if (this.activePlayerStyleElem && !this.surf.container) {
      if (this.activePlayerStyleElem.parentNode) {
        this.activePlayerStyleElem.parentNode.removeChild(this.activePlayerStyleElem);
      }
      this.activePlayerStyleElem = null;
    }

    if (this.styleElem && !this.surf.container) {
      this.styleElem.parentNode.removeChild(this.styleElem);
      this.styleElem = null;
    }
  }

  _preventBodyScroll(rawEvent) {
    this.logger.log('_preventBodyScroll(rawEvent)', rawEvent);

    const e = rawEvent || window.event;
    // Don't prevent scrolling when having minimized player state and surf behind without an iframe.
    if (this.__restoreMinimized && !this.surf.frame) {
      return true;
    }

    // Do not prevent if the event has more than one touch
    // (usually meaning this is a multi touch gesture like pinch to zoom).
    if (e.touches.length > 1) {
      return true;
    }

    if (e.preventDefault) {
      e.preventDefault();
    }

    return false;
  }

  _processReceivedMessage = (message) => {
    if (message && message.eventName) {
      this._handlePlayerEvent(message);
    }
  };

  _receiveSurfFrameMessage(message, event) {
    if (!this.surf.frame || this.surf.frame.contentWindow !== event.source) {
      // if source is not the iframe
      if (
        !message ||
        (
          message.eventName !== PLAYER_EVENTS.ON_PROGRESS &&
          message.eventName !== PLAYER_EVENTS.TRACKING_POINT &&
          message.eventName !== PLAYER_EVENTS.MINI_PLAYER_MOVE
        )
      ) {
        this.logger.log(
          '_receiveSurfFrameMessage() exit',
          event && event.data,
        );
      }
      return;
    }

    const shouldLogEvent = !message || message.eventName !== PLAYER_EVENTS.ON_PROGRESS;
    if (shouldLogEvent) this.logger.logDev('_receiveSurfFrameMessage() Event from surf frame', message);

    if (message && message.eventName) {
      this._handleSurfFrameEvent(message);
    }
  }

  _processAppDevEnvMessage(message, event) {
    // App framework messages proxy layer passing data between player and app dev env
    if (
      this._isAppDevEnv &&
      typeof event.data === 'object' &&
      typeof event.data.eventType === 'string' &&
      event.data.eventType.startsWith('bambuserAppDev:')
    ) {
      if (event.source === window.parent) {
        // Forward app dev env message to player
        this.player.frame.contentWindow.postMessage(event.data, '*');
      } else if (event.source === this.player.frame.contentWindow) {
        // Forward player message to app dev env
        window.parent.postMessage(event.data, '*');
      }
    }
  }

  _sendPlayerEventResponse(receivedEvent, responseData) {
    // prettier-ignore
    receivedEvent.responseId
      ? this._sendMessageToPlayer(PLAYER_EVENTS.RESPONSE,
        {
          id: receivedEvent.responseId,
          responseData,
          embedInstanceKey: receivedEvent?.embedInstanceKey,
        })
      : this.logger.logProd(`Received response from embedding page for event '${
        receivedEvent.eventName}', but no response was expected. Ignoring it!`);
  }

  _getTimelineForTracking() {
    const { currentBroadcastId, currentTime, isLive, serverTimeAtCurrentPosition } = this.timeline;
    return {
      broadcastId: currentBroadcastId,
      isLive,
      relativeTime: currentTime,
      serverTime: serverTimeAtCurrentPosition,
    };
  }

  /**
   * Removes any potentially incriminating fields from
   * a configuration object
   * @param {object} config
   * @returns {object} clean config
   */
  _cleanPotentialPiiDataFromConfig(config) {
    const configClone = JSON.parse(JSON.stringify(config));

    delete configClone.chatName;

    return configClone;
  }

  _dispatchGlobalTrackingEvent(trackingPoint) {
    const event = new CustomEvent('bambuser-liveshop-tracking-point', { detail: trackingPoint });
    window.dispatchEvent(event);
    if (!this.config.withGA) return;
    bamlsTag('event', trackingPoint.event, bamlsTagData(trackingPoint.data));
  }

  _trackOnInteraction(interactionType, data = {}, opts = {}) {
    this._trackMetric('on-interaction', {
      ...data,
      interactionType,
    }, opts);
  }

  _trackMetric(eventName, data = {}, opts = {}) {
    if (INTERNAL_EMBED_ORIGINS.test(window.location.origin) && this.config._collectMetrics === false) {
      return;
    }
    if (!eventName) {
      this.logger.logProd("Can't track metric without providing an eventName");
      return;
    }

    const commonData = {
      insertId: uuidv4(),
      orgId: this.playerSettings.orgId,
      showId: this.config.eventId,
      userId: this._trackingContext?.userId,
      sessionId: this._trackingContext?.sessionId,
      source: 'embed',
      playerVersion: this.playerVersion || 0,
      referrerUrl: window.location.origin,
      timeline: this._getTimelineForTracking(),
      ...(this.trackingTags?.length > 0 && { customerTags: this.trackingTags }),
      ...data,
    };
    const trackingData = { eventType: eventName, ...commonData };
    const globalTrackingPointData = { event: eventName, data: { ...commonData } };

    if (this.config.eventId) {
      this._trackMetricEmit(trackingData, opts?.flushImmediately);
    }
    this._dispatchGlobalTrackingEvent(globalTrackingPointData);
  }

  _trackMetricEmit(data, flushImmediately = false) {
    if (!process.env.METRICS_ENDPOINT) return;
    data.clientTimestamp = new Date().toISOString();

    // Save the data to the queue
    window._bamMetricsQueue.push(data);

    if (flushImmediately) {
      this._flushMetrics();
      return;
    }

    // If we have a pending send, don't do anything
    if (this._metricsSendHandle) return;

    // Schedule a send
    this._scheduleFlush();
  }

  _scheduleFlush() {
    this._metricsSendHandle = window.setTimeout(() => {
      this._metricsSendHandle = null;
      this._flushMetrics();
    }, 2000);
  }

  _flushMetrics() {
    // Rotate the queue
    // Pick the first 20 items and send them. Let the rest stay in the queue.
    // See the /metric endpoint in collect for the cutoff for the batch size
    // (currently 20).
    const queueCopy = window._bamMetricsQueue.slice();
    window._bamMetricsQueue = queueCopy.slice(20);
    const metricsQueue = queueCopy.slice(0, 20);
    if (!metricsQueue.length) return;

    const stringifiedData = JSON.stringify({ bundle: metricsQueue });

    const fetchAvailable = (window.Request && window.fetch);
    const fetchKeepaliveAvailable = fetchAvailable && 'keepalive' in window.Request.prototype;

    let sendBeaconSuccessful = false;
    if (!fetchAvailable && window.navigator && window.navigator.sendBeacon) {
      try {
        // Navigator.sendBeacon returns true if the user agent is able to successfully queue the data
        // for transfer but leaves no garueantee that the data will be successfully transferred. WebViews
        // which is presented in apps could very well have their Navigator.sendBeacon functionality wing clipped
        // by anti-tracking permission settings or the web browser running some adblock/uBlock origin type extensions.
        // Question is if we'll ever be able to detect this?
        sendBeaconSuccessful = window.navigator.sendBeacon(process.env.METRICS_ENDPOINT, stringifiedData);
      } catch (err) {
        // Let's use the last fallback below
      }
    }

    if (!sendBeaconSuccessful) {
      window.fetch(process.env.METRICS_ENDPOINT, {
        method: 'POST',
        body: stringifiedData,
        ...(fetchKeepaliveAvailable && { keepalive: true }),
        mode: 'cors',
      }).catch((error) => {
        console.log(error);
      });
    }

    // If we have more data in the queue, schedule another flush
    if (window._bamMetricsQueue.length) {
      this._scheduleFlush();
    }
  }

  _getUrlFromWindow() {
    const { origin, pathname, hash, search } = window.location;
    let urlParams = new URLSearchParams(search);
    urlParams.delete('autoplayLiveShopping'); // Don't start any auto-playing show please!
    urlParams = urlParams.toString();
    return `${origin}${pathname}${urlParams.length > 0 ? `?${urlParams}` : ''}${hash}`;
  }

  _sendCustomCSSToPlayer() {
    const css = window.__bambuserPlayerCSSOverrides;
    if (typeof css !== 'string') return;
    this._sendMessageToPlayer(PLAYER_EVENTS.CSS_OVERRIDES, { css });
  }

  _onPlayerThemeUpdate(theme) {
    this.logger.log('_onPlayerThemeUpdate()', theme);
    if (!theme || typeof theme !== 'object') return;
    this._playerTheme = theme;
  }

  _getTranslation(key, defaultValue) {
    return this._playerTheme?.generic?.[key] || defaultValue;
  }

  _handlePlayerEvent(message) {
    const { eventName, data } = message || {};
    const { event: trackingEvent, data: trackingData } = data || {};

    // In these cases the "eventName" is the "responseId" we tagged the
    // request with in playerApiCall()
    if (typeof this._pendingRequestsToPlayer[eventName] === 'function') {
      this._pendingRequestsToPlayer[eventName](data);
      this._pendingRequestsToPlayer[eventName] = null;
      delete this._pendingRequestsToPlayer[eventName];

      // We've now handled the response, so we can return early
      return;
    }

    switch (eventName) {
      case PLAYER_EVENTS.SET_CLOSE_ICON:
        BambuserLiveShopping.closeIcon = data.replace('<svg', '<svg class="theming-icon"');
        BambuserLiveShopping.closeIconV2 = data; // For version 2 UI we don't apply weird resizing like above
        break;
      case PLAYER_EVENTS.SET_RESTORE_ICON:
        BambuserLiveShopping.restoreIcon = data.replace('<svg', '<svg class="theming-icon"');
        break;
      case PLAYER_EVENTS.SET_VIDEO_STATE:
        Object.assign(this.video, data);
        if (this.__calculateMinimizedView) this.__calculateMinimizedView();
        break;
      case PLAYER_EVENTS.PLAYBACK_STATUS:
        this.miniPlayer.playbackStatus = data || {};
        this.updateMiniPlayerPlaybackControls();
        break;
      case PLAYER_EVENTS.FOCUS_LEAVE:
        this.surf.frame.contentWindow.focus();
        break;
      case PLAYER_EVENTS.FOCUS_FIRST_OUTSIDE_IFRAME:
        this.onMiniPlayerMouseEnterLeave({ type: 'mouseenter' });
        // setTimeout required otherwise we focus the restore/close button before it's re-rendered by the emitted event
        window.setTimeout(() => {
          const firstButton = this.miniPlayer.expandButton || this.miniPlayer.closeButton;
          firstButton && firstButton.focus();
        }, 0);
        break;
      case PLAYER_EVENTS.FOCUS_LAST_OUTSIDE_IFRAME:
        this.onMiniPlayerMouseEnterLeave({ type: 'mouseenter' });
        // setTimeout required otherwise we focus the close button before it's re-rendered by the emitted event
        window.setTimeout(() => {
          const lastButton = this.miniPlayer.closeButton || this.miniPlayer.expandButton;
          if (!lastButton) return;
          // For pointer-triggered minimizes, suppress the visible focus ring so touch/mouse users
          // don't see an unexpected outline, while still moving focus out of the now-untabbable iframe.
          if (data?.fromKeyboard === false) {
            lastButton.classList.add('livecommerce-focus-no-ring');
            if (this._pointerFocusCleanup) this._pointerFocusCleanup();
            const cleanup = () => lastButton.classList.remove('livecommerce-focus-no-ring');
            lastButton.addEventListener('keydown', cleanup, { once: true });
            this._pointerFocusCleanup = () => lastButton.removeEventListener('keydown', cleanup);
          }
          lastButton.focus({ preventScroll: true });
        }, 0);
        break;
      case PLAYER_EVENTS.MINI_PLAYER_DRAG_START:
        this._miniPlayerDragStart();
        break;
      case PLAYER_EVENTS.MINI_PLAYER_MOVE:
        if (typeof this.__handleMoveMinimizedPlayer === 'function') {
          this.__handleMoveMinimizedPlayer(data);
        }
        break;
      case PLAYER_EVENTS.MINI_PLAYER_DRAG_END:
        this._miniPlayerDragEnd();
        break;
      case PLAYER_EVENTS.MINIMIZED_ACTION_OVERLAY:
        const closeMinimizeButton = document.querySelector('.livecommerce-close-minimized');
        if (closeMinimizeButton) {
          const { withBackground } = data || {};
          closeMinimizeButton.style.background = withBackground ? 'none' : '';
        }
        break;
      case PLAYER_EVENTS.ON_PROGRESS:
        this.timeline = data;
        break;
      case PLAYER_EVENTS.READY:
        Object.assign(this.playerSettings, data || {});
        this._playerReady = true;

        if (!this._trackingContext && (this.playerSettings.userId || this.playerSettings.sessionId)) this._trackingContext = {};
        // Update the userId/sessionId in the tracking context if they are provided by the player
        if (this.playerSettings.userId) this._trackingContext.userId = this.playerSettings.userId;
        if (this.playerSettings.sessionId) this._trackingContext.sessionId = this.playerSettings.sessionId;

        this.showPlayer();
        this._trackMetric('on-configuration', {
          configuration: JSON.stringify(
            this._cleanPotentialPiiDataFromConfig(this.playerDelegate.config.rawConfig)
          ),
          subscribedEvents: this.playerDelegate.getSupportedEvents(),
          ...(Array.isArray(this.earlyPlayerAPIMessages) && {
            earlyPlayerAPIMessages: JSON.stringify(this.earlyPlayerAPIMessages.map(message => message.eventName)),
          }),
        });
        // We initialize the surf behind with the landing page
        if (this.playerSettings.withMinimizeSupport && !this.surf.frame && !this.config.minimizeEventOnly) {
          this.logger.log('Pre-initializing surf');
          this.surf.skipNextBrowserHistoryEntry = true;
          this.surfTo(this._getUrlFromWindow());
        }
        this.__didTrackUnload = false;
        this.__windowUnloadHandler = () => {
          if (this.__didTrackUnload) return;
          this.__didTrackUnload = true;
          this._trackOnInteraction('close', { actionOrigin: 'window' }, { flushImmediately: true });
        };
        window.addEventListener('unload', this.__windowUnloadHandler);
        window.addEventListener('beforeunload', this.__windowUnloadHandler);

        // Apply custom CSS styles
        this._sendCustomCSSToPlayer();

        if (Array.isArray(this.earlyPlayerAPIMessages)) {
          for (const message of this.earlyPlayerAPIMessages) {
            // We need to re-send the messages to the player now that it's ready
            // Right now sending only some messages, not to introduce any side effects
            if ([
              PLAYER_EVENTS.SHOW_PRODUCT_VIEW,
              PLAYER_EVENTS.PAUSE,
              PLAYER_EVENTS.PROVIDE_PRODUCT_DATA,
              PLAYER_EVENTS.EVENT_INFO_STATE,
              PLAYER_EVENTS.SET_ROOT_FONT_SIZE,
              PLAYER_EVENTS.APPLY_THEME,
            ].includes(message.eventName)) {
              this._sendMessageToPlayer(message.eventName, message.data);
            }
          }
          this.earlyPlayerAPIMessages = null;
        }

        if (this.config.startFullscreen) {
          this.enterPlayerFullscreen();
        }

        break;
      case PLAYER_EVENTS.CLOSE:
        this._emitEvent(PLAYER_EVENTS_PUBLIC.CLOSE);
        // If player is configured to never act upon close event from within the player,
        // we return early and let the integration code handle the close event.
        if (this.config.closeEventOnly && data?.actionOrigin !== 'api') return;

        this.exitPlayerFullscreen();
        if (this.__windowUnloadHandler) {
          window.removeEventListener('unload', this.__windowUnloadHandler);
          delete this.__windowUnloadHandler;
        }
        window.removeEventListener('resize', this.__calculateMinimizedView);
        this.unloadPlayerApp();

        if (this.config.destroyOnAppUnload) {
          // Force destroying the player after a timeout in case the unload sequence gets stuck.
          this._forceDestroyPlayerTimeoutHandle = window.setTimeout(() => {
            this.logger.logProd('Forcing player destroy after timeout');
            this.destroy();
          }, 50);
        }
        break;
      case PLAYER_EVENTS.APP_UNLOADED:
        // Player app has been unloaded, we can safely destroy the player iframe
        if (this.config.destroyOnAppUnload) {
          this.destroy();
        }
        break;
      case PLAYER_EVENTS.MINIMIZE:
        this._emitEvent(PLAYER_EVENTS_PUBLIC.MINIMIZE, data || {});
        // If player is configured to never act upon minimize event from within the player,
        // we return early and let the integration code handle the minimize event.
        if (this.config.minimizeEventOnly) return;

        let targetUrl = (data || {}).url || null;
        // When not having a surf behind iframe and no explicit url to load,
        // allow current page url to load inside the frame.
        if (!targetUrl && !this.surf.frame && this.config.surfBehindMode !== MANUAL) {
          targetUrl = this._getUrlFromWindow();
          this.surf.skipNextBrowserHistoryEntry = true;
        }

        if (targetUrl) {
          this._emitEvent(PLAYER_EVENTS_PUBLIC.OPEN_URL, { url: targetUrl, target: '_blank' });
          // Fallback to open in new window when device doesn't support surf behind iframe (and minimized player).
          if (!this.playerSettings.withMinimizeSupport) {
            // If player config is configured to not use window open, let's not.
            return !this.config._disableWindowOpen && window.open(targetUrl, '_blank');
          }
          this.surfTo(targetUrl);
        }
        this.minimize();
        break;
      case PLAYER_EVENTS.MINI_PLAYER_RESTORE:
        if (typeof this.__restoreMinimized === 'function') {
          this.__restoreMinimized();
        }
        break;
      case PLAYER_EVENTS.FULLSCREEN_ENTER:
        this.enterPlayerFullscreen();
        break;
      case PLAYER_EVENTS.FULLSCREEN_EXIT:
        this.exitPlayerFullscreen();
        break;
      // Some in-app browsers disallow the player iframe to do this redirect
      // in which case we need to initiate the redirect from embed.js instead
      case PLAYER_EVENTS.OPEN_EXTERNAL_BROWSER:
        if (data.url) {
          window.location.href = data.url;
        }
        break;
      case PLAYER_EVENTS.THEME_UPDATE:
        this._onPlayerThemeUpdate(data);
        this.updateMiniPlayerPlaybackControls();
        break;
      case PLAYER_EVENTS.TRACKING_POINT:
        if (!data) return;

        this._dispatchGlobalTrackingEvent(data);

        if (!this.shouldUpdateTrackingCookies) return;

        if (trackingData) {
          if (!this._trackingContext.showId && trackingData.showId) this._trackingContext.showId = trackingData.showId;
          if (!this._trackingContext.orgId && trackingData.orgId) this._trackingContext.orgId = trackingData.orgId;
          if (trackingEvent === 'on-play') this._trackingContext.showWasPlayed = true;
        }

        this._manageCookieRefreshInterval(trackingEvent);

        this._updateTrackingCookies();

        break;
      case PLAYER_EVENTS.TRACKING_SEND_EVENT:
        if (this.config.eventId) {
          this._trackMetricEmit({ source: 'embed', ...trackingData });
        }
        break;
      case PLAYER_EVENTS.RESET_BASE_PAGE_SCROLL:
        window.scrollTo(0, 0);
        break;
      case PLAYER_EVENTS.EXTERNAL_PLAYER_CONTROLLER_NEW:
        this.playerDelegate.setupNewPlayerController(data.id);
        break;
      case PLAYER_EVENTS.EXTERNAL_PLAYER_CONTROLLER_CHANGE:
        const isNewController = data.id !== this.currentPlayerController();
        this.playerDelegate.changeCurrentPlayerController(data.id);
        if (isNewController) this._emitEvent(PLAYER_EVENTS_PUBLIC.EXTERNAL_PLAYER_CONTROLLER_CHANGE, data);
        break;
      case PLAYER_EVENTS.LOAD:
        // Make sure to update tracking service with added tags prior to player beign loaded
        this.setTrackingTags(this.trackingTags);

        if (this._shouldClosePlayerOnLastBackNavigation) {
          // Prime the BrowserHistoryManager to have at least depth 1 when player
          // is shown so that one back navigation will close the player instead of
          // going back to previous page.
          BrowserHistoryManager.setDepth(1);
        }
        break;
      case PLAYER_EVENTS.REQUEST_EVENT_CHANGE:
        if (data?.eventId) {
          // Treat promoted-show navigation as a fresh player; we don't support
          // going Back through every promoted show opened in the same iframe.
          BrowserHistoryManager.setDepth(this._shouldClosePlayerOnLastBackNavigation ? 1 : 0);
        }
        this._setAppContextInCurrentPlayer(data);
        break;
      case PLAYER_EVENTS.NAVIGATION_SET_DEPTH:
        // The player has pushed or popped inside its internal navigation stack.
        // Update the browser history depth accordingly.
        const { depth } = data || {};
        if (typeof depth === 'number' && depth >= 0) {
          // Artificially increase depth by one if the player is configured to close
          // on last back navigation rather than navigate back to previous browser history entry.
          const nextDepth = depth + (this._shouldClosePlayerOnLastBackNavigation ? 1 : 0);
          BrowserHistoryManager.setDepth(nextDepth);
        }
        break;
      case PLAYER_EVENTS.SEO_SCHEMA_ANNOUNCEMENT:
        // If we're not in the root window, then chances are that we receive this for some inline player
        // which exists in a surf-behind-type context. Let's not inject into this iframes DOM.
        if (window !== window.top) return;
        const { schema } = data || {};
        injectLdJsonSchemaScriptTag(this.config.eventId, schema);
        break;
      default:
        // Unhandled player event
        break;
    }

    if (PLAYER_EVENTS_TO_FORWARD.includes(eventName)) {
      // Event emitted to the embedding page should not include the player event prefix.
      const cleanEventName = eventName.startsWith(PLAYER_EVENT_PREFIX)
        ? eventName.substr(PLAYER_EVENT_PREFIX.length)
        : eventName;
      this._emitEvent(cleanEventName, data, this._sendPlayerEventResponse.bind(this, message));
    }
  }

  loadShow(eventId, isNewShow = false) {
    // prettier-ignore
    this.logger.log(
      'loadShow(eventId)', eventId,
      '__restoreMinimized', !!this.__restoreMinimized,
      'eventId === config.eventId', eventId === this.config.eventId,
      'isNewShow', isNewShow,
    );

    if (!isNewShow && eventId === this.config.eventId && this.__restoreMinimized) {
      this.__restoreMinimized();
    } else {
      this.show(true);
    }
  }

  // TODO: How to pause native video/audio elements?
  _pauseOtherPlaybacks() {
    // Stop all iframe embeds of 3rd-party video/audio services
    const iframes = document.querySelectorAll('iframe');
    // eslint-disable-next-line
    for (const iframe of iframes) {
      if (iframe.src && iframe.src.startsWith('https://www.youtube.com/embed')) {
        if (iframe.src.indexOf('enablejsapi=1') >= 0) {
          iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), '*');
        } // else {} // TODO: if youtube iframe doesn't support JS API, reload and ensure that autoplay param is off??
      } // else {} // TODO: What other services should be stopped?
    }

    // Stop all iframe embeds of 3rd-party video/audio services in surf behind frame
    // It isn't possible to query and send post messages to iframes inside the surf behind frame for some reason,
    // so add a temporary script that executes in the surf frame context instead to archive the task at hand.
    if (this.surf.frame) {
      try {
        const surfIframeScript = this.surf.frame.contentWindow.document.createElement('script');
        surfIframeScript.innerHTML = `
        (function() {
          let iframes = document.querySelectorAll('iframe');
          for (let i = 0; i < iframes.length; i++) {
            let iframe = iframes[i];
            if (iframe.src && iframe.src.indexOf('https://www.youtube.com/embed') === 0) {
              if (iframe.src.indexOf('enablejsapi=1') >= 0) {
                iframe.contentWindow.postMessage(JSON.stringify({
                  event: 'command',
                  func: 'pauseVideo',
                }), '*');
              }
            }
          }
          document.currentScript.parentNode.removeChild(document.currentScript);
        })();`;
        this.surf.frame.contentWindow.document.body.appendChild(surfIframeScript);
      } catch (e) {
        /* Just swallow any cross-domain errors... */
      }
    }
  }

  _handleSurfFrameEvent({ eventName, data }) {
    this.logger.log('_handleSurfFrameEvent(eventName, data)', { eventName, data });

    switch (eventName) {
      case SURF_FRAME_EVENTS.REQUEST_OPEN_SHOW:
        const { config, requestId, fromNode } = data;

        if (!config || !requestId) {
          return;
        }

        const currentEventId = this.config.eventId;
        const isBambuserLiveShoppingExposed = !!window.__currentBambuserLiveShoppingOverlayPlayer;

        this.logger.log('Request open show', { currentEventId, isBambuserLiveShoppingExposed });

        // If autoplay & same show id than current playing show, we don't interrupt the current show for the same one
        // We skip autoplay to not reload the player while preloading the same page in the surf behind iframe
        // We can remove the check on the eventId once all our customers are not using autoplay code on a button click
        if (!fromNode && config.eventId === currentEventId && isBambuserLiveShoppingExposed) {
          this.logger.log('Skip autoplay');
          if (this.__restoreMinimized) {
            this.__restoreMinimized();
          }
        } else {
          const { eventId } = config;
          const isNewShow = eventId !== currentEventId;
          if (isNewShow) {
            // Mounting the API again will make new call to the mountPoint with the new eventId and make the
            // getProviderConfig method return a config specific to that new event
            this.mountPlayerAPI(eventId, true, this.player?.container ?? null);
          }

          // Fetch the provider config for the show which is about to load
          // and merge it with any new configs which might be passed in the request
          // (notably deeplink is an interesting property which we want the new player config
          // to properly update).
          const providerConfig = this.getProviderConfig();
          // Allow to disable autoplay from the player configuration (only if autoplay is allowed for the customer)
          if (config.allowShareAutoplay) {
            if (providerConfig.allowShareAutoplay === false) config.allowShareAutoplay = false;
          } else if (providerConfig.allowShareAutoplay === true) {
            console.warn(
              WARNING.PLAYER_CONFIG_IGNORED_BY_ORGANIZATION_CONFIG('allowShareAutoplay', 'autoplayHandler'),
            );
          }
          this.config = Object.assign(providerConfig, config);

          this.loadShow(eventId, isNewShow);
        }

        const message = JSON.stringify({
          eventName: SURF_FRAME_EVENTS.ACCEPTED_REQUEST_OPEN_SHOW,
          data: { requestId },
        });
        this.surf.frame.contentWindow.postMessage(message, '*');
        break;
      default:
        this.logger.logDev('Received unhandled surf frame event', eventName);
        break;
    }
  }

  _sendMessageToPlayer(eventName, data = null) {
    if (eventName === PLAYER_EVENTS.PROVIDE_WISHLIST_STATUS) {
      // To let bamProduct know about this event
      // We need to post the message to the window
      data.embedInstanceKey = this.instanceKey;
      window.postMessage(JSON.stringify({ eventName, data }), '*');
    }

    if (!this._frames.length && (!this.player.frame || !this._playerReady)) {
      if (!this.earlyPlayerAPIMessages) {
        this.earlyPlayerAPIMessages = [];
      }
      const isHighContrastToggle = (e) =>
        e === PLAYER_EVENTS.ENABLE_HIGH_CONTRAST || e === PLAYER_EVENTS.DISABLE_HIGH_CONTRAST;
      if (!this.earlyPlayerAPIMessages.some((message) =>
        message?.eventName === eventName ||
        (isHighContrastToggle(eventName) && isHighContrastToggle(message?.eventName)),
      )) {
        this.earlyPlayerAPIMessages.push({ eventName, data });
      } else if (isHighContrastToggle(eventName)) {
        // Toggle pair: latest call wins regardless of polarity, so a
        // disable → enable sequence does not get silently dropped.
        this.earlyPlayerAPIMessages = this.earlyPlayerAPIMessages.map((message) =>
          isHighContrastToggle(message?.eventName) ? { eventName, data } : message,
        );
      } else if (eventName === PLAYER_EVENTS.SET_ROOT_FONT_SIZE) {
        // Last-write-wins: replace the queued font size with the latest call
        this.earlyPlayerAPIMessages = this.earlyPlayerAPIMessages.map((message) =>
          message?.eventName === PLAYER_EVENTS.SET_ROOT_FONT_SIZE ? { eventName, data } : message,
        );
      } else if (eventName === PLAYER_EVENTS.APPLY_THEME) {
        // patchTheme() always sends the full cumulative override on every call,
        // so the latest queued message already contains every prior update.
        this.earlyPlayerAPIMessages = this.earlyPlayerAPIMessages.map((message) =>
          message?.eventName === PLAYER_EVENTS.APPLY_THEME ? { eventName, data } : message,
        );
      } else if (eventName === PLAYER_EVENTS.PROVIDE_PRODUCT_DATA) {
        this.earlyPlayerAPIMessages = this.earlyPlayerAPIMessages.map((message) => {
          if (message?.eventName !== PLAYER_EVENTS.PROVIDE_PRODUCT_DATA) {
            return message;
          }

          const prevMessageProducts = message.data?.products || [];
          const newMessageProducts = data?.products || [];
          // Merge products in the new message into products in the previous message
          const products = Object.values({
            ...Object.fromEntries(prevMessageProducts.map(({ _id, ...rest }) => [ _id, { _id, ...rest } ])),
            ...Object.fromEntries(newMessageProducts.map(({ _id, ...rest }) => [ _id, { _id, ...rest } ])),
          });

          return { eventName, data: { ...message.data, products } };
        });
      }

      return;
    }

    this.logger.logDev('WILL EMIT TO PLAYER', eventName, data);

    const payload = { eventName };
    if (data) {
      payload.data = data;
    }

    // Post message to all frames associated with this embed instance
    for (const frame of this._frames) {
      frame?.contentWindow?.postMessage(JSON.stringify(payload), '*');
    }
  }

  _emitEvent(eventName, ...args) {
    // handle the million versions of args
    // TODO: Can we clean this up?
    const event = {};
    let [data = null, callback = null] = args;

    if (typeof data === 'function') {
      callback = data;
      data = null;
    }

    if (data !== null) {
      event.data = data;
    }

    if (typeof callback === 'function') {
      event.callback = callback;
    }

    // actually send the event
    this.playerDelegate.pushEvent(eventName, event);

    if (typeof window.BambuserLiveShoppingEventHandler === 'function') {
      // legacy
      window.BambuserLiveShoppingEventHandler({ type: eventName, player: this, ...event });
    }
  }

  _updateTrackingCookies() {
    if (!this.shouldUpdateTrackingCookies) return;

    const { cookie: options = {}, isVod } = this.config;
    const { domain, activityCookieTTLDays } = options;
    const cookieConf = {
      expires: 365,
      ...(domain && { domain }),
    };
    const { orgId, userId, showId, sessionId, showWasPlayed } = this._trackingContext;

    if (orgId) {
      writeCookie(TRACKING_COOKIES.CUSTOMER_ID, orgId, cookieConf);
    }

    if (userId) {
      writeCookie(TRACKING_COOKIES.USER_ID, userId, cookieConf);
    }

    if (sessionId) {
      const inThirtyMinutes = new Date(new Date().getTime() + 30 * 60 * 1000);
      writeCookie(TRACKING_COOKIES.SESSION_ID, sessionId, { ...cookieConf, expires: inThirtyMinutes });
    }

    const expiresInDays = activityCookieTTLDays > 0 ? activityCookieTTLDays : 30;
    if (showId && !isVod) {
      const activityCookieConf = { ...cookieConf, expires: expiresInDays };
      writeCookie(TRACKING_COOKIES.SHOW_ID, showId, activityCookieConf);
      if (showWasPlayed) {
        writeCookie(TRACKING_COOKIES.LAST_INTERACTION_TIMESTAMP, new Date().getTime(), activityCookieConf);
      }
    }
    if (isVod) {
      let localizedConversionTrackingExpiryCookie = {};
      const expiryTimestamp = Date.now() + (86400000 * expiresInDays);
      try {
        localizedConversionTrackingExpiryCookie = JSON.parse(
          readCookie(TRACKING_COOKIES.LOCALIZED_CONVERSION_TRACKING_EXPIRY) || {}
        );
        if (localizedConversionTrackingExpiryCookie.constructor !== Object) {
          localizedConversionTrackingExpiryCookie = {};
        }
      } catch (e) {
        // cookie did not contain parseable JSON so we can ignore it and start over
      }

      const locationKey = process.env.PROJECT_ID.endsWith('eu') ? 'eu' : 'us';
      localizedConversionTrackingExpiryCookie[locationKey] = expiryTimestamp;

      const latestExpiryTimestamp = Math.max(...Object.values(localizedConversionTrackingExpiryCookie));

      writeCookie(
        TRACKING_COOKIES.LOCALIZED_CONVERSION_TRACKING_EXPIRY,
        JSON.stringify(localizedConversionTrackingExpiryCookie),
        {
          ...cookieConf,
          expires: new Date(latestExpiryTimestamp),
        }
      );
    }
  }

  /**
   * Starts, resets or clears the 60 second interval after which cookies will be refreshed.
   * If trackingEvent is "on-stop" the interval is only cleared, not restarted.
   * When started it runs until manually stopped or restarted again.
   * @param {METRIC_TYPE} trackingEvent
   */
  _manageCookieRefreshInterval(trackingEvent) {
    if (!this.shouldUpdateTrackingCookies) return;

    this._clearCookieRefreshInterval();

    if (!this._trackingContext.showWasPlayed) return; // We do not want to automatically refresh cookies unless show was played
    if (trackingEvent === 'on-stop') return; // We do not want to start a new interval after show has finished

    this._cookieRefreshInterval = window.setInterval(() => {
      this._updateTrackingCookies();
    }, 60 * 1000);
  }

  _clearCookieRefreshInterval() {
    if (!this._cookieRefreshInterval) return;

    window.clearInterval(this._cookieRefreshInterval);
    delete this._cookieRefreshInterval;
  }

  _viewportChangeHandler(e) {
    const height = e.target.viewport?.height ?? e.target.height;

    this._sendMessageToPlayer(PLAYER_EVENTS.CURRENT_BASEPAGE_VIEWPORT, { height });
  }

  _pushPageScrollTopHandler() {
    // Throttle scroll event - clear existing interval or send current scroll pos directly if first event
    this.__pushPageScrollTopTimeout
      ? window.clearTimeout(this.__pushPageScrollTopTimeout)
      : this._sendMessageToPlayer(PLAYER_EVENTS.CURRENT_BASEPAGE_SCROLLTOP, { scrollTop: window.scrollY });

    // Throttle scroll event - Wait some time until sending current scroll pos as scroll events can fire crazy often.
    this.__pushPageScrollTopTimeout = window.setTimeout(() => {
      delete this.__pushPageScrollTopTimeout;
      this._sendMessageToPlayer(PLAYER_EVENTS.CURRENT_BASEPAGE_SCROLLTOP, { scrollTop: window.scrollY });
    }, 150);
  }

  _disableParentScroll({ fixPlayerPos = false } = {}) {
    if (fixPlayerPos && this._isMinimized && this.player?.container) {
      // Before disabling the scroll on the page if the player is in mini-player mode and this is called due to some interaction
      // on the player, such as dragging or clicking some buttons we need to add some margins to the right and bottom of the player
      // container with the value of the size of the possibly visible scrollbars, otherwise when we set overflow hidden on the
      // documentElement and body these scroll bars will be hidden and causing the player to "jump".
      this.player.container.style.setProperty('margin-right', `${window.innerWidth - document.documentElement.clientWidth}px`);
      this.player.container.style.setProperty('margin-bottom', `${window.innerHeight - document.documentElement.clientHeight}px`);
    }

    document.documentElement.style.setProperty('overflow', 'hidden', 'important'); // documentElement = html
    document.body.style.setProperty('overflow', 'hidden', 'important');
    document.body.style.overscrollBehavior = 'none'; // Disables pull-to-refresh and overscroll glow effect
  }

  _restoreParentScroll() {
    if (this.player?.container) {
      // Remove any margins added to the player container in _disableParentScroll before restoring scroll
      // this way the player won't jump when we restore the scroll
      this.player.container.style.setProperty('margin-right', 'unset');
      this.player.container.style.setProperty('margin-bottom', 'unset');
    }

    document.documentElement.style.setProperty('overflow', ''); // documentElement = html
    document.body.style.setProperty('overflow', '');
    document.body.style.overscrollBehavior = '';
  }

  /*
   * Get the current player controller
   */
  currentPlayerController() {
    return this.playerDelegate.currentPlayerController();
  }

  /*
   * Get all available player controllers
   */
  playerControllers() {
    return this.playerDelegate.playerControllers();
  }

  /*
   * Function to provide the video players event data to the LVS web app player controller
   */
  externalPlayerControllerEvent(data) {
    this._sendMessageToPlayer(PLAYER_EVENTS.EXTERNAL_PLAYER_CONTROLLER_EVENTS, data);
  }

  isAutoplayAllowed() {
    return true;
  }
}

BambuserLiveShopping.restoreIcon = defaultRestoreIcon;
BambuserLiveShopping.closeIcon = defaultCloseIcon;
BambuserLiveShopping.closeIconV2 = defaultCloseIconV2;

export default BambuserLiveShopping;
